export type Language = {
  code: string;
  name: string;
  nativeName: string;
  dir?: 'rtl' | 'ltr';
};

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski' },
  { code: 'sr', name: 'Serbian', nativeName: 'Srpski' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'bs', name: 'Bosnian', nativeName: 'Bosanski' },
  { code: 'mk', name: 'Macedonian', nativeName: 'Македонски' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
];

export interface Translations {
  nav: {
    features: string;
    howItWorks: string;
    contact: string;
    openApp: string;
  };
  hero: {
    badge: string;
    tagline: string;
    subtitle: string;
    description: string;
    openWebApp: string;
    learnMore: string;
    stats: {
      updateInterval: string;
      webBased: string;
      liveTracking: string;
    };
    scroll: string;
  };
  preview: {
    title: string;
    subtitle: string;
  };
  features: {
    badge: string;
    title: string;
    subtitle: string;
    items: {
      liveGps: { title: string; description: string };
      webApp: { title: string; description: string };
      traccar: { title: string; description: string };
      updates: { title: string; description: string };
      profiles: { title: string; description: string };
      radio: { title: string; description: string };
    };
  };
  howItWorks: {
    badge: string;
    title: string;
    subtitle: string;
    steps: {
      open: { title: string; description: string };
      profile: { title: string; description: string };
      server: { title: string; description: string };
      track: { title: string; description: string };
    };
    tryNow: string;
  };
  webOnly: {
    badge: string;
    title: string;
    titleHighlight: string;
    description: string;
    benefits: string[];
    cards: {
      browser: { title: string; desc: string };
      mobile: { title: string; desc: string };
      instant: { title: string; desc: string };
      secure: { title: string; desc: string };
    };
  };
  traccarSection: {
    title: string;
    description: string;
    tags: { osmand: string; traccar: string; realtime: string };
  };
  contact: {
    badge: string;
    title: string;
    description: string;
    whatsapp: string;
    whatsappSub: string;
    telegram: string;
    telegramSub: string;
    availability: string;
  };
  cta: {
    title: string;
    description: string;
    openApp: string;
    contactSales: string;
  };
  footer: {
    description: string;
    quickLinks: string;
    links: {
      openApp: string;
      features: string;
      howItWorks: string;
      contact: string;
    };
    contactTitle: string;
    copyright: string;
    serviceOnline: string;
  };
}

const translations: Record<string, Translations> = {
  en: {
    nav: { features: 'Features', howItWorks: 'How It Works', contact: 'Contact', openApp: 'Open App' },
    hero: {
      badge: 'Live GPS Tracking — Web App',
      tagline: 'GPS Tracker',
      subtitle: 'Live Location to Traccar',
      description: 'Personal GPS tracker that transmits your live location every 10 seconds to your Traccar OsmAnd server. Start, track, and confirm transmissions in real time.',
      openWebApp: 'Open Web App',
      learnMore: 'Learn More',
      stats: { updateInterval: 'Update Interval', webBased: 'Web Based', liveTracking: 'Live Tracking' },
      scroll: 'Scroll',
    },
    preview: { title: 'See It In Action', subtitle: 'Clean, intuitive interface designed for drivers' },
    features: {
      badge: 'Features',
      title: 'Everything You Need',
      subtitle: 'A complete GPS tracking solution built for drivers, fleet managers, and anyone who needs reliable real-time location sharing.',
      items: {
        liveGps: { title: 'Live GPS Tracking', description: 'Transmits your precise location every 10 seconds to your Traccar OsmAnd server in real time.' },
        webApp: { title: 'Web App — No Install', description: 'Works directly in your browser. No app store download needed. Open and start tracking instantly.' },
        traccar: { title: 'Traccar Compatible', description: 'Seamlessly integrates with your existing Traccar / OsmAnd server. Just enter your server URL and device ID.' },
        updates: { title: '10-Second Updates', description: 'Ultra-frequent position updates every 10 seconds ensure you always have the most current location data.' },
        profiles: { title: 'Driver Profiles', description: 'Sign in with your account, create a named driver profile, or track anonymously using just your device ID.' },
        radio: { title: 'Online Radio', description: 'Built-in online radio player to keep you entertained during long drives — all in one app.' },
      },
    },
    howItWorks: {
      badge: 'How It Works',
      title: 'Up and Running in Minutes',
      subtitle: 'No installation, no app store. Just open your browser and start tracking.',
      steps: {
        open: { title: 'Open the Web App', description: 'Navigate to navigacija.easydrive.app in any modern browser on your phone or computer. No installation required.' },
        profile: { title: 'Set Up Your Profile', description: 'Sign in to your account, create a new driver profile, or continue anonymously. Your choice.' },
        server: { title: 'Enter Server Details', description: 'Input your Traccar OsmAnd server URL and device ID. The app remembers your settings for next time.' },
        track: { title: 'Start Tracking', description: 'Hit the LIVE button and your location starts transmitting every 10 seconds. Confirm transmissions in real time.' },
      },
      tryNow: "Try It Now — It's Free",
    },
    webOnly: {
      badge: 'Web-Only App',
      title: 'No App Store.',
      titleHighlight: 'Just Open & Track.',
      description: 'EasyDrive is a Progressive Web App (PWA) — it runs entirely in your browser. No downloads, no updates to manage, no storage space used.',
      benefits: [
        'Works on any device with a modern browser',
        'No Google Play or App Store required',
        'Always up-to-date — no manual updates',
        'Add to home screen for app-like experience',
        'Background tracking with service worker',
      ],
      cards: {
        browser: { title: 'Browser-Based', desc: 'Open in Chrome, Safari, Firefox — any browser works' },
        mobile: { title: 'Mobile Ready', desc: 'Optimized for smartphones with touch-friendly UI' },
        instant: { title: 'Instant Access', desc: 'No waiting for downloads or installations' },
        secure: { title: 'Secure', desc: 'HTTPS encrypted, your data stays private' },
      },
    },
    traccarSection: {
      title: 'Traccar & OsmAnd Compatible',
      description: 'Already running a Traccar server? EasyDrive connects directly to your existing infrastructure using the OsmAnd protocol. Just enter your server URL and device ID.',
      tags: { osmand: 'OsmAnd Protocol', traccar: 'Traccar Server', realtime: 'Real-time Updates' },
    },
    contact: {
      badge: 'Contact Us',
      title: 'Get In Touch',
      description: 'Have questions about EasyDrive? Want to purchase a license or get support? Reach us directly on your preferred messaging platform.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Chat with us',
      telegram: 'Telegram',
      telegramSub: 'Message us',
      availability: 'Available Monday – Friday, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Start Tracking Today',
      description: 'Open EasyDrive in your browser and start transmitting your live location to your Traccar server in under 2 minutes.',
      openApp: 'Open EasyDrive',
      contactSales: 'Contact Sales',
    },
    footer: {
      description: 'Personal GPS tracker that transmits your live location every 10 seconds to your Traccar OsmAnd server.',
      quickLinks: 'Quick Links',
      links: { openApp: 'Open App', features: 'Features', howItWorks: 'How It Works', contact: 'Contact' },
      contactTitle: 'Contact',
      copyright: 'EasyDrive GPS Tracker. All rights reserved.',
      serviceOnline: 'Service Online',
    },
  },
  sl: {
    nav: { features: 'Funkcije', howItWorks: 'Kako deluje', contact: 'Kontakt', openApp: 'Odpri aplikacijo' },
    hero: {
      badge: 'Sledenje GPS v živo — Spletna aplikacija',
      tagline: 'GPS Sledilnik',
      subtitle: 'Lokacija v živo na Traccar',
      description: 'Osebni GPS sledilnik, ki vsake 10 sekund pošilja vašo lokacijo v živo na vaš Traccar OsmAnd strežnik. Začnite, sledite in potrdite prenose v realnem času.',
      openWebApp: 'Odpri spletno aplikacijo',
      learnMore: 'Več informacij',
      stats: { updateInterval: 'Interval posodobitev', webBased: 'Spletna aplikacija', liveTracking: 'Sledenje v živo' },
      scroll: 'Pomakni se',
    },
    preview: { title: 'Oglejte si v akciji', subtitle: 'Čist, intuitiven vmesnik, zasnovan za voznike' },
    features: {
      badge: 'Funkcije',
      title: 'Vse kar potrebujete',
      subtitle: 'Popolna rešitev za sledenje GPS, zasnovana za voznike, upravljavce flot in vse, ki potrebujejo zanesljivo deljenje lokacije v realnem času.',
      items: {
        liveGps: { title: 'Sledenje GPS v živo', description: 'Vsake 10 sekund pošilja vašo natančno lokacijo na vaš Traccar OsmAnd strežnik v realnem času.' },
        webApp: { title: 'Spletna aplikacija — brez namestitve', description: 'Deluje neposredno v vašem brskalniku. Ni potreben prenos iz trgovine z aplikacijami. Odprite in takoj začnite slediti.' },
        traccar: { title: 'Združljivo s Traccar', description: 'Brezhibno se integrira z vašim obstoječim Traccar / OsmAnd strežnikom. Samo vnesite URL strežnika in ID naprave.' },
        updates: { title: 'Posodobitve vsakih 10 sekund', description: 'Ultra-pogoste posodobitve položaja vsakih 10 sekund zagotavljajo, da imate vedno najnovejše podatke o lokaciji.' },
        profiles: { title: 'Profili voznikov', description: 'Prijavite se s svojim računom, ustvarite poimenovan profil voznika ali sledite anonimno samo z ID-jem naprave.' },
        radio: { title: 'Spletni radio', description: 'Vgrajeni predvajalnik spletnega radia, ki vas zabava med dolgimi vožnjami — vse v eni aplikaciji.' },
      },
    },
    howItWorks: {
      badge: 'Kako deluje',
      title: 'Pripravljeni v minutah',
      subtitle: 'Brez namestitve, brez trgovine z aplikacijami. Samo odprite brskalnik in začnite slediti.',
      steps: {
        open: { title: 'Odprite spletno aplikacijo', description: 'Pojdite na navigacija.easydrive.app v katerem koli sodobnem brskalniku na telefonu ali računalniku. Namestitev ni potrebna.' },
        profile: { title: 'Nastavite profil', description: 'Prijavite se v račun, ustvarite nov profil voznika ali nadaljujte anonimno. Vaša izbira.' },
        server: { title: 'Vnesite podatke strežnika', description: 'Vnesite URL vašega Traccar OsmAnd strežnika in ID naprave. Aplikacija si zapomni vaše nastavitve.' },
        track: { title: 'Začnite slediti', description: 'Pritisnite gumb LIVE in vaša lokacija se začne prenašati vsakih 10 sekund. Potrdite prenose v realnem času.' },
      },
      tryNow: 'Preizkusite zdaj — brezplačno',
    },
    webOnly: {
      badge: 'Samo spletna aplikacija',
      title: 'Brez trgovine z aplikacijami.',
      titleHighlight: 'Samo odprite in sledite.',
      description: 'EasyDrive je progresivna spletna aplikacija (PWA) — deluje v celoti v vašem brskalniku. Brez prenosov, brez upravljanja posodobitev, brez porabe prostora.',
      benefits: [
        'Deluje na kateri koli napravi s sodobnim brskalnikom',
        'Ni potreben Google Play ali App Store',
        'Vedno posodobljeno — brez ročnih posodobitev',
        'Dodajte na domači zaslon za izkušnjo podobno aplikaciji',
        'Sledenje v ozadju s service workerjem',
      ],
      cards: {
        browser: { title: 'Brskalniška aplikacija', desc: 'Odprite v Chromu, Safariju, Firefoxu — deluje vsak brskalnik' },
        mobile: { title: 'Mobilno pripravljeno', desc: 'Optimizirano za pametne telefone s prijaznim vmesnikom na dotik' },
        instant: { title: 'Takojšen dostop', desc: 'Brez čakanja na prenose ali namestitve' },
        secure: { title: 'Varno', desc: 'Šifrirano s HTTPS, vaši podatki ostanejo zasebni' },
      },
    },
    traccarSection: {
      title: 'Združljivo s Traccar in OsmAnd',
      description: 'Že imate Traccar strežnik? EasyDrive se neposredno poveže z vašo obstoječo infrastrukturo z uporabo protokola OsmAnd. Samo vnesite URL strežnika in ID naprave.',
      tags: { osmand: 'Protokol OsmAnd', traccar: 'Strežnik Traccar', realtime: 'Posodobitve v realnem času' },
    },
    contact: {
      badge: 'Kontaktirajte nas',
      title: 'Stopite v stik',
      description: 'Imate vprašanja o EasyDrive? Želite kupiti licenco ali dobiti podporo? Kontaktirajte nas neposredno na vaši priljubljeni platformi za sporočanje.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Klepetajte z nami',
      telegram: 'Telegram',
      telegramSub: 'Pišite nam',
      availability: 'Na voljo ponedeljek – petek, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Začnite slediti danes',
      description: 'Odprite EasyDrive v brskalniku in začnite prenašati svojo lokacijo v živo na Traccar strežnik v manj kot 2 minutah.',
      openApp: 'Odpri EasyDrive',
      contactSales: 'Kontaktirajte prodajo',
    },
    footer: {
      description: 'Osebni GPS sledilnik, ki vsake 10 sekund pošilja vašo lokacijo v živo na vaš Traccar OsmAnd strežnik.',
      quickLinks: 'Hitre povezave',
      links: { openApp: 'Odpri aplikacijo', features: 'Funkcije', howItWorks: 'Kako deluje', contact: 'Kontakt' },
      contactTitle: 'Kontakt',
      copyright: 'EasyDrive GPS Sledilnik. Vse pravice pridržane.',
      serviceOnline: 'Storitev v spletu',
    },
  },
  hr: {
    nav: { features: 'Značajke', howItWorks: 'Kako radi', contact: 'Kontakt', openApp: 'Otvori aplikaciju' },
    hero: {
      badge: 'GPS praćenje uživo — Web aplikacija',
      tagline: 'GPS Praćenje',
      subtitle: 'Lokacija uživo na Traccar',
      description: 'Osobni GPS tracker koji šalje vašu lokaciju uživo svakih 10 sekundi na vaš Traccar OsmAnd server. Pokrenite, pratite i potvrdite prijenose u stvarnom vremenu.',
      openWebApp: 'Otvori web aplikaciju',
      learnMore: 'Saznaj više',
      stats: { updateInterval: 'Interval ažuriranja', webBased: 'Web aplikacija', liveTracking: 'Praćenje uživo' },
      scroll: 'Pomakni se',
    },
    preview: { title: 'Pogledajte u akciji', subtitle: 'Čisto, intuitivno sučelje dizajnirano za vozače' },
    features: {
      badge: 'Značajke',
      title: 'Sve što trebate',
      subtitle: 'Kompletno rješenje za GPS praćenje namijenjeno vozačima, upraviteljima flota i svima koji trebaju pouzdano dijeljenje lokacije u stvarnom vremenu.',
      items: {
        liveGps: { title: 'GPS praćenje uživo', description: 'Šalje vašu preciznu lokaciju svakih 10 sekundi na vaš Traccar OsmAnd server u stvarnom vremenu.' },
        webApp: { title: 'Web aplikacija — bez instalacije', description: 'Radi izravno u vašem pregledniku. Nije potrebno preuzimanje iz trgovine aplikacija. Otvorite i odmah počnite pratiti.' },
        traccar: { title: 'Kompatibilno s Traccar', description: 'Besprijekorno se integrira s vašim postojećim Traccar / OsmAnd serverom. Samo unesite URL servera i ID uređaja.' },
        updates: { title: 'Ažuriranja svakih 10 sekundi', description: 'Ultra-česta ažuriranja položaja svakih 10 sekundi osiguravaju da uvijek imate najnovije podatke o lokaciji.' },
        profiles: { title: 'Profili vozača', description: 'Prijavite se s računom, stvorite imenovan profil vozača ili pratite anonimno koristeći samo ID uređaja.' },
        radio: { title: 'Online radio', description: 'Ugrađeni online radio player koji vas zabavlja tijekom dugih vožnji — sve u jednoj aplikaciji.' },
      },
    },
    howItWorks: {
      badge: 'Kako radi',
      title: 'Spremni za rad za nekoliko minuta',
      subtitle: 'Bez instalacije, bez trgovine aplikacija. Samo otvorite preglednik i počnite pratiti.',
      steps: {
        open: { title: 'Otvorite web aplikaciju', description: 'Idite na navigacija.easydrive.app u bilo kojem modernom pregledniku na telefonu ili računalu. Instalacija nije potrebna.' },
        profile: { title: 'Postavite profil', description: 'Prijavite se na račun, stvorite novi profil vozača ili nastavite anonimno. Vaš izbor.' },
        server: { title: 'Unesite podatke servera', description: 'Unesite URL vašeg Traccar OsmAnd servera i ID uređaja. Aplikacija pamti vaše postavke.' },
        track: { title: 'Počnite pratiti', description: 'Pritisnite gumb LIVE i vaša lokacija počinje se prenositi svakih 10 sekundi. Potvrdite prijenose u stvarnom vremenu.' },
      },
      tryNow: 'Isprobajte sada — besplatno',
    },
    webOnly: {
      badge: 'Samo web aplikacija',
      title: 'Bez trgovine aplikacija.',
      titleHighlight: 'Samo otvorite i pratite.',
      description: 'EasyDrive je progresivna web aplikacija (PWA) — radi u potpunosti u vašem pregledniku. Bez preuzimanja, bez upravljanja ažuriranjima, bez korištenja prostora za pohranu.',
      benefits: [
        'Radi na bilo kojem uređaju s modernim preglednikom',
        'Nije potreban Google Play ili App Store',
        'Uvijek ažurirano — bez ručnih ažuriranja',
        'Dodajte na početni zaslon za iskustvo poput aplikacije',
        'Praćenje u pozadini s service workerom',
      ],
      cards: {
        browser: { title: 'Temeljeno na pregledniku', desc: 'Otvorite u Chromeu, Safariju, Firefoxu — svaki preglednik radi' },
        mobile: { title: 'Mobilno spremno', desc: 'Optimizirano za pametne telefone s korisničkim sučeljem prilagođenim dodiru' },
        instant: { title: 'Trenutni pristup', desc: 'Bez čekanja na preuzimanja ili instalacije' },
        secure: { title: 'Sigurno', desc: 'HTTPS enkripcija, vaši podaci ostaju privatni' },
      },
    },
    traccarSection: {
      title: 'Kompatibilno s Traccar i OsmAnd',
      description: 'Već koristite Traccar server? EasyDrive se izravno povezuje s vašom postojećom infrastrukturom koristeći OsmAnd protokol. Samo unesite URL servera i ID uređaja.',
      tags: { osmand: 'OsmAnd protokol', traccar: 'Traccar server', realtime: 'Ažuriranja u stvarnom vremenu' },
    },
    contact: {
      badge: 'Kontaktirajte nas',
      title: 'Stupite u kontakt',
      description: 'Imate pitanja o EasyDrive? Želite kupiti licencu ili dobiti podršku? Kontaktirajte nas izravno na vašoj omiljenoj platformi za razmjenu poruka.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Razgovarajte s nama',
      telegram: 'Telegram',
      telegramSub: 'Pišite nam',
      availability: 'Dostupno ponedjeljak – petak, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Počnite pratiti danas',
      description: 'Otvorite EasyDrive u pregledniku i počnite prenositi svoju lokaciju uživo na Traccar server za manje od 2 minute.',
      openApp: 'Otvori EasyDrive',
      contactSales: 'Kontaktirajte prodaju',
    },
    footer: {
      description: 'Osobni GPS tracker koji šalje vašu lokaciju uživo svakih 10 sekundi na vaš Traccar OsmAnd server.',
      quickLinks: 'Brze veze',
      links: { openApp: 'Otvori aplikaciju', features: 'Značajke', howItWorks: 'Kako radi', contact: 'Kontakt' },
      contactTitle: 'Kontakt',
      copyright: 'EasyDrive GPS Praćenje. Sva prava pridržana.',
      serviceOnline: 'Usluga online',
    },
  },
  sr: {
    nav: { features: 'Funkcije', howItWorks: 'Kako radi', contact: 'Kontakt', openApp: 'Otvori aplikaciju' },
    hero: {
      badge: 'GPS praćenje uživo — Web aplikacija',
      tagline: 'GPS Praćenje',
      subtitle: 'Lokacija uživo na Traccar',
      description: 'Lični GPS tracker koji šalje vašu lokaciju uživo svakih 10 sekundi na vaš Traccar OsmAnd server. Pokrenite, pratite i potvrdite prenose u realnom vremenu.',
      openWebApp: 'Otvori web aplikaciju',
      learnMore: 'Saznaj više',
      stats: { updateInterval: 'Interval ažuriranja', webBased: 'Web aplikacija', liveTracking: 'Praćenje uživo' },
      scroll: 'Pomeri se',
    },
    preview: { title: 'Pogledajte u akciji', subtitle: 'Čist, intuitivan interfejs dizajniran za vozače' },
    features: {
      badge: 'Funkcije',
      title: 'Sve što vam treba',
      subtitle: 'Kompletno rešenje za GPS praćenje namenjeno vozačima, menadžerima flota i svima koji trebaju pouzdano deljenje lokacije u realnom vremenu.',
      items: {
        liveGps: { title: 'GPS praćenje uživo', description: 'Šalje vašu preciznu lokaciju svakih 10 sekundi na vaš Traccar OsmAnd server u realnom vremenu.' },
        webApp: { title: 'Web aplikacija — bez instalacije', description: 'Radi direktno u vašem pregledaču. Nije potrebno preuzimanje iz prodavnice aplikacija. Otvorite i odmah počnite pratiti.' },
        traccar: { title: 'Kompatibilno sa Traccar', description: 'Besprekorno se integriše sa vašim postojećim Traccar / OsmAnd serverom. Samo unesite URL servera i ID uređaja.' },
        updates: { title: 'Ažuriranja svakih 10 sekundi', description: 'Ultra-česta ažuriranja položaja svakih 10 sekundi osiguravaju da uvek imate najnovije podatke o lokaciji.' },
        profiles: { title: 'Profili vozača', description: 'Prijavite se sa nalogom, kreirajte imenovan profil vozača ili pratite anonimno koristeći samo ID uređaja.' },
        radio: { title: 'Online radio', description: 'Ugrađeni online radio plejer koji vas zabavlja tokom dugih vožnji — sve u jednoj aplikaciji.' },
      },
    },
    howItWorks: {
      badge: 'Kako radi',
      title: 'Spremni za rad za nekoliko minuta',
      subtitle: 'Bez instalacije, bez prodavnice aplikacija. Samo otvorite pregledač i počnite pratiti.',
      steps: {
        open: { title: 'Otvorite web aplikaciju', description: 'Idite na navigacija.easydrive.app u bilo kom modernom pregledaču na telefonu ili računaru. Instalacija nije potrebna.' },
        profile: { title: 'Podesite profil', description: 'Prijavite se na nalog, kreirajte novi profil vozača ili nastavite anonimno. Vaš izbor.' },
        server: { title: 'Unesite podatke servera', description: 'Unesite URL vašeg Traccar OsmAnd servera i ID uređaja. Aplikacija pamti vaša podešavanja.' },
        track: { title: 'Počnite pratiti', description: 'Pritisnite dugme LIVE i vaša lokacija počinje da se prenosi svakih 10 sekundi. Potvrdite prenose u realnom vremenu.' },
      },
      tryNow: 'Isprobajte sada — besplatno',
    },
    webOnly: {
      badge: 'Samo web aplikacija',
      title: 'Bez prodavnice aplikacija.',
      titleHighlight: 'Samo otvorite i pratite.',
      description: 'EasyDrive je progresivna web aplikacija (PWA) — radi u potpunosti u vašem pregledaču. Bez preuzimanja, bez upravljanja ažuriranjima, bez korišćenja prostora za skladištenje.',
      benefits: [
        'Radi na bilo kom uređaju sa modernim pregledačem',
        'Nije potreban Google Play ili App Store',
        'Uvek ažurirano — bez ručnih ažuriranja',
        'Dodajte na početni ekran za iskustvo poput aplikacije',
        'Praćenje u pozadini sa service workerom',
      ],
      cards: {
        browser: { title: 'Zasnovano na pregledaču', desc: 'Otvorite u Chromu, Safariju, Firefoxu — svaki pregledač radi' },
        mobile: { title: 'Mobilno spremno', desc: 'Optimizovano za pametne telefone sa korisničkim interfejsom prilagođenim dodiru' },
        instant: { title: 'Trenutni pristup', desc: 'Bez čekanja na preuzimanja ili instalacije' },
        secure: { title: 'Bezbedno', desc: 'HTTPS enkripcija, vaši podaci ostaju privatni' },
      },
    },
    traccarSection: {
      title: 'Kompatibilno sa Traccar i OsmAnd',
      description: 'Već koristite Traccar server? EasyDrive se direktno povezuje sa vašom postojećom infrastrukturom koristeći OsmAnd protokol. Samo unesite URL servera i ID uređaja.',
      tags: { osmand: 'OsmAnd protokol', traccar: 'Traccar server', realtime: 'Ažuriranja u realnom vremenu' },
    },
    contact: {
      badge: 'Kontaktirajte nas',
      title: 'Stupite u kontakt',
      description: 'Imate pitanja o EasyDrive? Želite da kupite licencu ili dobijete podršku? Kontaktirajte nas direktno na vašoj omiljenoj platformi za razmenu poruka.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Razgovarajte sa nama',
      telegram: 'Telegram',
      telegramSub: 'Pišite nam',
      availability: 'Dostupno ponedeljak – petak, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Počnite pratiti danas',
      description: 'Otvorite EasyDrive u pregledaču i počnite da prenosite svoju lokaciju uživo na Traccar server za manje od 2 minuta.',
      openApp: 'Otvori EasyDrive',
      contactSales: 'Kontaktirajte prodaju',
    },
    footer: {
      description: 'Lični GPS tracker koji šalje vašu lokaciju uživo svakih 10 sekundi na vaš Traccar OsmAnd server.',
      quickLinks: 'Brze veze',
      links: { openApp: 'Otvori aplikaciju', features: 'Funkcije', howItWorks: 'Kako radi', contact: 'Kontakt' },
      contactTitle: 'Kontakt',
      copyright: 'EasyDrive GPS Praćenje. Sva prava zadržana.',
      serviceOnline: 'Usluga online',
    },
  },
  de: {
    nav: { features: 'Funktionen', howItWorks: 'So funktioniert es', contact: 'Kontakt', openApp: 'App öffnen' },
    hero: {
      badge: 'Live-GPS-Tracking — Web-App',
      tagline: 'GPS-Tracker',
      subtitle: 'Live-Standort zu Traccar',
      description: 'Persönlicher GPS-Tracker, der Ihren Live-Standort alle 10 Sekunden an Ihren Traccar OsmAnd-Server überträgt. Starten, verfolgen und Übertragungen in Echtzeit bestätigen.',
      openWebApp: 'Web-App öffnen',
      learnMore: 'Mehr erfahren',
      stats: { updateInterval: 'Aktualisierungsintervall', webBased: 'Webbasiert', liveTracking: 'Live-Tracking' },
      scroll: 'Scrollen',
    },
    preview: { title: 'In Aktion sehen', subtitle: 'Saubere, intuitive Benutzeroberfläche für Fahrer' },
    features: {
      badge: 'Funktionen',
      title: 'Alles was Sie brauchen',
      subtitle: 'Eine vollständige GPS-Tracking-Lösung für Fahrer, Flottenmanager und alle, die zuverlässige Echtzeit-Standortfreigabe benötigen.',
      items: {
        liveGps: { title: 'Live-GPS-Tracking', description: 'Überträgt Ihren genauen Standort alle 10 Sekunden in Echtzeit an Ihren Traccar OsmAnd-Server.' },
        webApp: { title: 'Web-App — keine Installation', description: 'Funktioniert direkt in Ihrem Browser. Kein App-Store-Download erforderlich. Öffnen und sofort mit dem Tracking beginnen.' },
        traccar: { title: 'Traccar-kompatibel', description: 'Integriert sich nahtlos in Ihren bestehenden Traccar / OsmAnd-Server. Geben Sie einfach Ihre Server-URL und Geräte-ID ein.' },
        updates: { title: '10-Sekunden-Updates', description: 'Ultra-häufige Positionsaktualisierungen alle 10 Sekunden stellen sicher, dass Sie immer die aktuellsten Standortdaten haben.' },
        profiles: { title: 'Fahrerprofile', description: 'Melden Sie sich mit Ihrem Konto an, erstellen Sie ein benanntes Fahrerprofil oder verfolgen Sie anonym mit Ihrer Geräte-ID.' },
        radio: { title: 'Online-Radio', description: 'Eingebauter Online-Radio-Player, der Sie bei langen Fahrten unterhält — alles in einer App.' },
      },
    },
    howItWorks: {
      badge: 'So funktioniert es',
      title: 'In Minuten einsatzbereit',
      subtitle: 'Keine Installation, kein App-Store. Öffnen Sie einfach Ihren Browser und beginnen Sie mit dem Tracking.',
      steps: {
        open: { title: 'Web-App öffnen', description: 'Navigieren Sie zu navigacija.easydrive.app in einem modernen Browser auf Ihrem Telefon oder Computer. Keine Installation erforderlich.' },
        profile: { title: 'Profil einrichten', description: 'Melden Sie sich bei Ihrem Konto an, erstellen Sie ein neues Fahrerprofil oder fahren Sie anonym fort. Ihre Wahl.' },
        server: { title: 'Serverdetails eingeben', description: 'Geben Sie Ihre Traccar OsmAnd-Server-URL und Geräte-ID ein. Die App merkt sich Ihre Einstellungen.' },
        track: { title: 'Tracking starten', description: 'Drücken Sie den LIVE-Button und Ihr Standort beginnt alle 10 Sekunden zu übertragen. Bestätigen Sie Übertragungen in Echtzeit.' },
      },
      tryNow: 'Jetzt ausprobieren — kostenlos',
    },
    webOnly: {
      badge: 'Nur Web-App',
      title: 'Kein App-Store.',
      titleHighlight: 'Einfach öffnen & tracken.',
      description: 'EasyDrive ist eine Progressive Web App (PWA) — sie läuft vollständig in Ihrem Browser. Keine Downloads, keine Updates zu verwalten, kein Speicherplatz verbraucht.',
      benefits: [
        'Funktioniert auf jedem Gerät mit einem modernen Browser',
        'Kein Google Play oder App Store erforderlich',
        'Immer aktuell — keine manuellen Updates',
        'Zum Startbildschirm hinzufügen für App-ähnliche Erfahrung',
        'Hintergrund-Tracking mit Service Worker',
      ],
      cards: {
        browser: { title: 'Browserbasiert', desc: 'In Chrome, Safari, Firefox öffnen — jeder Browser funktioniert' },
        mobile: { title: 'Mobilbereit', desc: 'Optimiert für Smartphones mit touch-freundlicher Benutzeroberfläche' },
        instant: { title: 'Sofortiger Zugriff', desc: 'Kein Warten auf Downloads oder Installationen' },
        secure: { title: 'Sicher', desc: 'HTTPS-verschlüsselt, Ihre Daten bleiben privat' },
      },
    },
    traccarSection: {
      title: 'Traccar & OsmAnd kompatibel',
      description: 'Betreiben Sie bereits einen Traccar-Server? EasyDrive verbindet sich direkt mit Ihrer bestehenden Infrastruktur über das OsmAnd-Protokoll. Geben Sie einfach Ihre Server-URL und Geräte-ID ein.',
      tags: { osmand: 'OsmAnd-Protokoll', traccar: 'Traccar-Server', realtime: 'Echtzeit-Updates' },
    },
    contact: {
      badge: 'Kontaktieren Sie uns',
      title: 'Nehmen Sie Kontakt auf',
      description: 'Haben Sie Fragen zu EasyDrive? Möchten Sie eine Lizenz kaufen oder Support erhalten? Erreichen Sie uns direkt auf Ihrer bevorzugten Messaging-Plattform.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Chatten Sie mit uns',
      telegram: 'Telegram',
      telegramSub: 'Schreiben Sie uns',
      availability: 'Verfügbar Montag – Freitag, 8:00 – 18:00 Uhr MEZ',
    },
    cta: {
      title: 'Beginnen Sie heute mit dem Tracking',
      description: 'Öffnen Sie EasyDrive in Ihrem Browser und beginnen Sie in weniger als 2 Minuten, Ihren Live-Standort an Ihren Traccar-Server zu übertragen.',
      openApp: 'EasyDrive öffnen',
      contactSales: 'Vertrieb kontaktieren',
    },
    footer: {
      description: 'Persönlicher GPS-Tracker, der Ihren Live-Standort alle 10 Sekunden an Ihren Traccar OsmAnd-Server überträgt.',
      quickLinks: 'Schnelllinks',
      links: { openApp: 'App öffnen', features: 'Funktionen', howItWorks: 'So funktioniert es', contact: 'Kontakt' },
      contactTitle: 'Kontakt',
      copyright: 'EasyDrive GPS-Tracker. Alle Rechte vorbehalten.',
      serviceOnline: 'Dienst online',
    },
  },
  bs: {
    nav: { features: 'Funkcije', howItWorks: 'Kako radi', contact: 'Kontakt', openApp: 'Otvori aplikaciju' },
    hero: {
      badge: 'GPS praćenje uživo — Web aplikacija',
      tagline: 'GPS Praćenje',
      subtitle: 'Lokacija uživo na Traccar',
      description: 'Lični GPS tracker koji šalje vašu lokaciju uživo svakih 10 sekundi na vaš Traccar OsmAnd server. Pokrenite, pratite i potvrdite prijenose u realnom vremenu.',
      openWebApp: 'Otvori web aplikaciju',
      learnMore: 'Saznaj više',
      stats: { updateInterval: 'Interval ažuriranja', webBased: 'Web aplikacija', liveTracking: 'Praćenje uživo' },
      scroll: 'Pomakni se',
    },
    preview: { title: 'Pogledajte u akciji', subtitle: 'Čisto, intuitivno sučelje dizajnirano za vozače' },
    features: {
      badge: 'Funkcije',
      title: 'Sve što trebate',
      subtitle: 'Kompletno rješenje za GPS praćenje namijenjeno vozačima, menadžerima flota i svima koji trebaju pouzdano dijeljenje lokacije u realnom vremenu.',
      items: {
        liveGps: { title: 'GPS praćenje uživo', description: 'Šalje vašu preciznu lokaciju svakih 10 sekundi na vaš Traccar OsmAnd server u realnom vremenu.' },
        webApp: { title: 'Web aplikacija — bez instalacije', description: 'Radi direktno u vašem pregledaču. Nije potrebno preuzimanje iz prodavnice aplikacija. Otvorite i odmah počnite pratiti.' },
        traccar: { title: 'Kompatibilno sa Traccar', description: 'Besprijekorno se integrira sa vašim postojećim Traccar / OsmAnd serverom. Samo unesite URL servera i ID uređaja.' },
        updates: { title: 'Ažuriranja svakih 10 sekundi', description: 'Ultra-česta ažuriranja položaja svakih 10 sekundi osiguravaju da uvijek imate najnovije podatke o lokaciji.' },
        profiles: { title: 'Profili vozača', description: 'Prijavite se sa računom, kreirajte imenovan profil vozača ili pratite anonimno koristeći samo ID uređaja.' },
        radio: { title: 'Online radio', description: 'Ugrađeni online radio plejer koji vas zabavlja tokom dugih vožnji — sve u jednoj aplikaciji.' },
      },
    },
    howItWorks: {
      badge: 'Kako radi',
      title: 'Spremni za rad za nekoliko minuta',
      subtitle: 'Bez instalacije, bez prodavnice aplikacija. Samo otvorite pregledač i počnite pratiti.',
      steps: {
        open: { title: 'Otvorite web aplikaciju', description: 'Idite na navigacija.easydrive.app u bilo kom modernom pregledaču na telefonu ili računaru. Instalacija nije potrebna.' },
        profile: { title: 'Podesite profil', description: 'Prijavite se na račun, kreirajte novi profil vozača ili nastavite anonimno. Vaš izbor.' },
        server: { title: 'Unesite podatke servera', description: 'Unesite URL vašeg Traccar OsmAnd servera i ID uređaja. Aplikacija pamti vaša podešavanja.' },
        track: { title: 'Počnite pratiti', description: 'Pritisnite dugme LIVE i vaša lokacija počinje da se prenosi svakih 10 sekundi. Potvrdite prijenose u realnom vremenu.' },
      },
      tryNow: 'Isprobajte sada — besplatno',
    },
    webOnly: {
      badge: 'Samo web aplikacija',
      title: 'Bez prodavnice aplikacija.',
      titleHighlight: 'Samo otvorite i pratite.',
      description: 'EasyDrive je progresivna web aplikacija (PWA) — radi u potpunosti u vašem pregledaču. Bez preuzimanja, bez upravljanja ažuriranjima, bez korištenja prostora za pohranu.',
      benefits: [
        'Radi na bilo kom uređaju sa modernim pregledačem',
        'Nije potreban Google Play ili App Store',
        'Uvijek ažurirano — bez ručnih ažuriranja',
        'Dodajte na početni ekran za iskustvo poput aplikacije',
        'Praćenje u pozadini sa service workerom',
      ],
      cards: {
        browser: { title: 'Zasnovano na pregledaču', desc: 'Otvorite u Chromu, Safariju, Firefoxu — svaki pregledač radi' },
        mobile: { title: 'Mobilno spremno', desc: 'Optimizirano za pametne telefone sa korisničkim sučeljem prilagođenim dodiru' },
        instant: { title: 'Trenutni pristup', desc: 'Bez čekanja na preuzimanja ili instalacije' },
        secure: { title: 'Sigurno', desc: 'HTTPS enkripcija, vaši podaci ostaju privatni' },
      },
    },
    traccarSection: {
      title: 'Kompatibilno sa Traccar i OsmAnd',
      description: 'Već koristite Traccar server? EasyDrive se direktno povezuje sa vašom postojećom infrastrukturom koristeći OsmAnd protokol. Samo unesite URL servera i ID uređaja.',
      tags: { osmand: 'OsmAnd protokol', traccar: 'Traccar server', realtime: 'Ažuriranja u realnom vremenu' },
    },
    contact: {
      badge: 'Kontaktirajte nas',
      title: 'Stupite u kontakt',
      description: 'Imate pitanja o EasyDrive? Želite kupiti licencu ili dobiti podršku? Kontaktirajte nas direktno na vašoj omiljenoj platformi za razmjenu poruka.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Razgovarajte sa nama',
      telegram: 'Telegram',
      telegramSub: 'Pišite nam',
      availability: 'Dostupno ponedjeljak – petak, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Počnite pratiti danas',
      description: 'Otvorite EasyDrive u pregledaču i počnite prenositi svoju lokaciju uživo na Traccar server za manje od 2 minute.',
      openApp: 'Otvori EasyDrive',
      contactSales: 'Kontaktirajte prodaju',
    },
    footer: {
      description: 'Lični GPS tracker koji šalje vašu lokaciju uživo svakih 10 sekundi na vaš Traccar OsmAnd server.',
      quickLinks: 'Brze veze',
      links: { openApp: 'Otvori aplikaciju', features: 'Funkcije', howItWorks: 'Kako radi', contact: 'Kontakt' },
      contactTitle: 'Kontakt',
      copyright: 'EasyDrive GPS Praćenje. Sva prava zadržana.',
      serviceOnline: 'Usluga online',
    },
  },
  mk: {
    nav: { features: 'Функции', howItWorks: 'Како работи', contact: 'Контакт', openApp: 'Отвори апликација' },
    hero: {
      badge: 'GPS следење во живо — Веб апликација',
      tagline: 'GPS Следач',
      subtitle: 'Локација во живо на Traccar',
      description: 'Личен GPS следач кој ја испраќа вашата локација во живо на секои 10 секунди до вашиот Traccar OsmAnd сервер. Започнете, следете и потврдете пренос во реално време.',
      openWebApp: 'Отвори веб апликација',
      learnMore: 'Дознај повеќе',
      stats: { updateInterval: 'Интервал на ажурирање', webBased: 'Веб апликација', liveTracking: 'Следење во живо' },
      scroll: 'Скролај',
    },
    preview: { title: 'Погледнете во акција', subtitle: 'Чист, интуитивен интерфејс дизајниран за возачи' },
    features: {
      badge: 'Функции',
      title: 'Сè што ви треба',
      subtitle: 'Комплетно решение за GPS следење наменето за возачи, менаџери на флоти и сите кои имаат потреба од сигурно споделување на локација во реално време.',
      items: {
        liveGps: { title: 'GPS следење во живо', description: 'Ја испраќа вашата прецизна локација на секои 10 секунди до вашиот Traccar OsmAnd сервер во реално време.' },
        webApp: { title: 'Веб апликација — без инсталација', description: 'Работи директно во вашиот прелистувач. Не е потребно преземање од продавница за апликации. Отворете и веднаш почнете да следите.' },
        traccar: { title: 'Компатибилно со Traccar', description: 'Беспрекорно се интегрира со вашиот постоечки Traccar / OsmAnd сервер. Само внесете го URL на серверот и ID на уредот.' },
        updates: { title: 'Ажурирања на секои 10 секунди', description: 'Ултра-чести ажурирања на позицијата на секои 10 секунди осигуруваат дека секогаш ги имате најновите податоци за локацијата.' },
        profiles: { title: 'Профили на возачи', description: 'Најавете се со вашата сметка, создадете именуван профил на возач или следете анонимно користејќи само ID на уредот.' },
        radio: { title: 'Онлајн радио', description: 'Вграден онлајн радио плеер кој ве забавува за време на долги возења — сè во една апликација.' },
      },
    },
    howItWorks: {
      badge: 'Како работи',
      title: 'Подготвени за работа за неколку минути',
      subtitle: 'Без инсталација, без продавница за апликации. Само отворете го прелистувачот и почнете да следите.',
      steps: {
        open: { title: 'Отворете ја веб апликацијата', description: 'Одете на navigacija.easydrive.app во кој било современ прелистувач на вашиот телефон или компјутер. Не е потребна инсталација.' },
        profile: { title: 'Поставете го профилот', description: 'Најавете се на сметката, создадете нов профил на возач или продолжете анонимно. Ваш избор.' },
        server: { title: 'Внесете ги деталите на серверот', description: 'Внесете го URL на вашиот Traccar OsmAnd сервер и ID на уредот. Апликацијата ги памети вашите поставки.' },
        track: { title: 'Почнете да следите', description: 'Притиснете го копчето LIVE и вашата локација почнува да се пренесува на секои 10 секунди. Потврдете пренос во реално време.' },
      },
      tryNow: 'Пробајте сега — бесплатно',
    },
    webOnly: {
      badge: 'Само веб апликација',
      title: 'Без продавница за апликации.',
      titleHighlight: 'Само отворете и следете.',
      description: 'EasyDrive е прогресивна веб апликација (PWA) — работи целосно во вашиот прелистувач. Без преземања, без управување со ажурирања, без користење простор за складирање.',
      benefits: [
        'Работи на кој било уред со современ прелистувач',
        'Не е потребен Google Play или App Store',
        'Секогаш ажурирано — без рачни ажурирања',
        'Додадете на почетен екран за искуство слично на апликација',
        'Следење во позадина со service worker',
      ],
      cards: {
        browser: { title: 'Базирано на прелистувач', desc: 'Отворете во Chrome, Safari, Firefox — секој прелистувач работи' },
        mobile: { title: 'Мобилно подготвено', desc: 'Оптимизирано за паметни телефони со интерфејс прилагоден на допир' },
        instant: { title: 'Моментален пристап', desc: 'Без чекање на преземања или инсталации' },
        secure: { title: 'Безбедно', desc: 'HTTPS шифрирање, вашите податоци остануваат приватни' },
      },
    },
    traccarSection: {
      title: 'Компатибилно со Traccar и OsmAnd',
      description: 'Веќе имате Traccar сервер? EasyDrive се поврзува директно со вашата постоечка инфраструктура користејќи го OsmAnd протоколот. Само внесете го URL на серверот и ID на уредот.',
      tags: { osmand: 'OsmAnd протокол', traccar: 'Traccar сервер', realtime: 'Ажурирања во реално време' },
    },
    contact: {
      badge: 'Контактирајте не',
      title: 'Стапете во контакт',
      description: 'Имате прашања за EasyDrive? Сакате да купите лиценца или да добиете поддршка? Контактирајте не директно на вашата омилена платформа за пораки.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Разговарајте со нас',
      telegram: 'Telegram',
      telegramSub: 'Пишете ни',
      availability: 'Достапно понеделник – петок, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Почнете да следите денес',
      description: 'Отворете EasyDrive во прелистувачот и почнете да ја пренесувате вашата локација во живо до Traccar серверот за помалку од 2 минути.',
      openApp: 'Отвори EasyDrive',
      contactSales: 'Контактирајте продажба',
    },
    footer: {
      description: 'Личен GPS следач кој ја испраќа вашата локација во живо на секои 10 секунди до вашиот Traccar OsmAnd сервер.',
      quickLinks: 'Брзи врски',
      links: { openApp: 'Отвори апликација', features: 'Функции', howItWorks: 'Како работи', contact: 'Контакт' },
      contactTitle: 'Контакт',
      copyright: 'EasyDrive GPS Следач. Сите права задржани.',
      serviceOnline: 'Услугата е онлајн',
    },
  },
  ru: {
    nav: { features: 'Функции', howItWorks: 'Как это работает', contact: 'Контакт', openApp: 'Открыть приложение' },
    hero: {
      badge: 'GPS-отслеживание в реальном времени — Веб-приложение',
      tagline: 'GPS-трекер',
      subtitle: 'Местоположение в реальном времени на Traccar',
      description: 'Персональный GPS-трекер, который передаёт ваше местоположение каждые 10 секунд на ваш сервер Traccar OsmAnd. Запускайте, отслеживайте и подтверждайте передачи в реальном времени.',
      openWebApp: 'Открыть веб-приложение',
      learnMore: 'Узнать больше',
      stats: { updateInterval: 'Интервал обновления', webBased: 'Веб-приложение', liveTracking: 'Отслеживание в реальном времени' },
      scroll: 'Прокрутить',
    },
    preview: { title: 'Смотрите в действии', subtitle: 'Чистый, интуитивный интерфейс для водителей' },
    features: {
      badge: 'Функции',
      title: 'Всё что вам нужно',
      subtitle: 'Полное решение для GPS-отслеживания для водителей, менеджеров автопарков и всех, кому нужен надёжный обмен местоположением в реальном времени.',
      items: {
        liveGps: { title: 'GPS-отслеживание в реальном времени', description: 'Передаёт ваше точное местоположение каждые 10 секунд на ваш сервер Traccar OsmAnd в реальном времени.' },
        webApp: { title: 'Веб-приложение — без установки', description: 'Работает прямо в вашем браузере. Загрузка из магазина приложений не требуется. Откройте и сразу начните отслеживание.' },
        traccar: { title: 'Совместимо с Traccar', description: 'Легко интегрируется с вашим существующим сервером Traccar / OsmAnd. Просто введите URL сервера и ID устройства.' },
        updates: { title: 'Обновления каждые 10 секунд', description: 'Сверхчастые обновления позиции каждые 10 секунд гарантируют, что у вас всегда есть самые актуальные данные о местоположении.' },
        profiles: { title: 'Профили водителей', description: 'Войдите в свой аккаунт, создайте именованный профиль водителя или отслеживайте анонимно, используя только ID устройства.' },
        radio: { title: 'Онлайн-радио', description: 'Встроенный плеер онлайн-радио, чтобы развлечь вас во время долгих поездок — всё в одном приложении.' },
      },
    },
    howItWorks: {
      badge: 'Как это работает',
      title: 'Готово к работе за несколько минут',
      subtitle: 'Без установки, без магазина приложений. Просто откройте браузер и начните отслеживание.',
      steps: {
        open: { title: 'Откройте веб-приложение', description: 'Перейдите на navigacija.easydrive.app в любом современном браузере на телефоне или компьютере. Установка не требуется.' },
        profile: { title: 'Настройте профиль', description: 'Войдите в аккаунт, создайте новый профиль водителя или продолжите анонимно. Ваш выбор.' },
        server: { title: 'Введите данные сервера', description: 'Введите URL вашего сервера Traccar OsmAnd и ID устройства. Приложение запомнит ваши настройки.' },
        track: { title: 'Начните отслеживание', description: 'Нажмите кнопку LIVE, и ваше местоположение начнёт передаваться каждые 10 секунд. Подтверждайте передачи в реальном времени.' },
      },
      tryNow: 'Попробуйте сейчас — бесплатно',
    },
    webOnly: {
      badge: 'Только веб-приложение',
      title: 'Без магазина приложений.',
      titleHighlight: 'Просто откройте и отслеживайте.',
      description: 'EasyDrive — это прогрессивное веб-приложение (PWA), которое работает полностью в вашем браузере. Без загрузок, без управления обновлениями, без использования места для хранения.',
      benefits: [
        'Работает на любом устройстве с современным браузером',
        'Google Play или App Store не требуются',
        'Всегда актуально — без ручных обновлений',
        'Добавьте на главный экран для опыта как у приложения',
        'Фоновое отслеживание с service worker',
      ],
      cards: {
        browser: { title: 'На основе браузера', desc: 'Открывайте в Chrome, Safari, Firefox — любой браузер работает' },
        mobile: { title: 'Готово для мобильных', desc: 'Оптимизировано для смартфонов с удобным сенсорным интерфейсом' },
        instant: { title: 'Мгновенный доступ', desc: 'Без ожидания загрузок или установок' },
        secure: { title: 'Безопасно', desc: 'HTTPS-шифрование, ваши данные остаются конфиденциальными' },
      },
    },
    traccarSection: {
      title: 'Совместимо с Traccar и OsmAnd',
      description: 'Уже используете сервер Traccar? EasyDrive подключается напрямую к вашей существующей инфраструктуре с использованием протокола OsmAnd. Просто введите URL сервера и ID устройства.',
      tags: { osmand: 'Протокол OsmAnd', traccar: 'Сервер Traccar', realtime: 'Обновления в реальном времени' },
    },
    contact: {
      badge: 'Свяжитесь с нами',
      title: 'Связаться',
      description: 'Есть вопросы об EasyDrive? Хотите купить лицензию или получить поддержку? Свяжитесь с нами напрямую на вашей предпочтительной платформе для обмена сообщениями.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Напишите нам',
      telegram: 'Telegram',
      telegramSub: 'Напишите нам',
      availability: 'Доступно понедельник – пятница, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Начните отслеживание сегодня',
      description: 'Откройте EasyDrive в браузере и начните передавать своё местоположение в реальном времени на сервер Traccar менее чем за 2 минуты.',
      openApp: 'Открыть EasyDrive',
      contactSales: 'Связаться с отделом продаж',
    },
    footer: {
      description: 'Персональный GPS-трекер, который передаёт ваше местоположение каждые 10 секунд на ваш сервер Traccar OsmAnd.',
      quickLinks: 'Быстрые ссылки',
      links: { openApp: 'Открыть приложение', features: 'Функции', howItWorks: 'Как это работает', contact: 'Контакт' },
      contactTitle: 'Контакт',
      copyright: 'EasyDrive GPS-трекер. Все права защищены.',
      serviceOnline: 'Сервис онлайн',
    },
  },
  uk: {
    nav: { features: 'Функції', howItWorks: 'Як це працює', contact: 'Контакт', openApp: 'Відкрити додаток' },
    hero: {
      badge: 'GPS-відстеження в реальному часі — Веб-додаток',
      tagline: 'GPS-трекер',
      subtitle: 'Місцезнаходження в реальному часі на Traccar',
      description: 'Персональний GPS-трекер, який передає ваше місцезнаходження кожні 10 секунд на ваш сервер Traccar OsmAnd. Запускайте, відстежуйте та підтверджуйте передачі в реальному часі.',
      openWebApp: 'Відкрити веб-додаток',
      learnMore: 'Дізнатися більше',
      stats: { updateInterval: 'Інтервал оновлення', webBased: 'Веб-додаток', liveTracking: 'Відстеження в реальному часі' },
      scroll: 'Прокрутити',
    },
    preview: { title: 'Дивіться в дії', subtitle: 'Чистий, інтуїтивний інтерфейс для водіїв' },
    features: {
      badge: 'Функції',
      title: 'Все що вам потрібно',
      subtitle: 'Повне рішення для GPS-відстеження для водіїв, менеджерів автопарків і всіх, кому потрібен надійний обмін місцезнаходженням у реальному часі.',
      items: {
        liveGps: { title: 'GPS-відстеження в реальному часі', description: 'Передає ваше точне місцезнаходження кожні 10 секунд на ваш сервер Traccar OsmAnd у реальному часі.' },
        webApp: { title: 'Веб-додаток — без встановлення', description: 'Працює прямо у вашому браузері. Завантаження з магазину додатків не потрібне. Відкрийте та одразу починайте відстеження.' },
        traccar: { title: 'Сумісно з Traccar', description: 'Легко інтегрується з вашим існуючим сервером Traccar / OsmAnd. Просто введіть URL сервера та ID пристрою.' },
        updates: { title: 'Оновлення кожні 10 секунд', description: 'Надчасті оновлення позиції кожні 10 секунд гарантують, що у вас завжди є найактуальніші дані про місцезнаходження.' },
        profiles: { title: 'Профілі водіїв', description: 'Увійдіть у свій обліковий запис, створіть іменований профіль водія або відстежуйте анонімно, використовуючи лише ID пристрою.' },
        radio: { title: 'Онлайн-радіо', description: 'Вбудований плеєр онлайн-радіо, щоб розважити вас під час довгих поїздок — все в одному додатку.' },
      },
    },
    howItWorks: {
      badge: 'Як це працює',
      title: 'Готово до роботи за кілька хвилин',
      subtitle: 'Без встановлення, без магазину додатків. Просто відкрийте браузер і починайте відстеження.',
      steps: {
        open: { title: 'Відкрийте веб-додаток', description: 'Перейдіть на navigacija.easydrive.app у будь-якому сучасному браузері на телефоні або комп\'ютері. Встановлення не потрібне.' },
        profile: { title: 'Налаштуйте профіль', description: 'Увійдіть в обліковий запис, створіть новий профіль водія або продовжте анонімно. Ваш вибір.' },
        server: { title: 'Введіть дані сервера', description: 'Введіть URL вашого сервера Traccar OsmAnd та ID пристрою. Додаток запам\'ятає ваші налаштування.' },
        track: { title: 'Почніть відстеження', description: 'Натисніть кнопку LIVE, і ваше місцезнаходження почне передаватися кожні 10 секунд. Підтверджуйте передачі в реальному часі.' },
      },
      tryNow: 'Спробуйте зараз — безкоштовно',
    },
    webOnly: {
      badge: 'Тільки веб-додаток',
      title: 'Без магазину додатків.',
      titleHighlight: 'Просто відкрийте та відстежуйте.',
      description: 'EasyDrive — це прогресивний веб-додаток (PWA), який працює повністю у вашому браузері. Без завантажень, без управління оновленнями, без використання місця для зберігання.',
      benefits: [
        'Працює на будь-якому пристрої з сучасним браузером',
        'Google Play або App Store не потрібні',
        'Завжди актуально — без ручних оновлень',
        'Додайте на головний екран для досвіду як у додатку',
        'Фонове відстеження з service worker',
      ],
      cards: {
        browser: { title: 'На основі браузера', desc: 'Відкривайте в Chrome, Safari, Firefox — будь-який браузер працює' },
        mobile: { title: 'Готово для мобільних', desc: 'Оптимізовано для смартфонів із зручним сенсорним інтерфейсом' },
        instant: { title: 'Миттєвий доступ', desc: 'Без очікування завантажень або встановлень' },
        secure: { title: 'Безпечно', desc: 'HTTPS-шифрування, ваші дані залишаються конфіденційними' },
      },
    },
    traccarSection: {
      title: 'Сумісно з Traccar і OsmAnd',
      description: 'Вже використовуєте сервер Traccar? EasyDrive підключається безпосередньо до вашої існуючої інфраструктури за допомогою протоколу OsmAnd. Просто введіть URL сервера та ID пристрою.',
      tags: { osmand: 'Протокол OsmAnd', traccar: 'Сервер Traccar', realtime: 'Оновлення в реальному часі' },
    },
    contact: {
      badge: 'Зв\'яжіться з нами',
      title: 'Зв\'язатися',
      description: 'Є питання про EasyDrive? Хочете купити ліцензію або отримати підтримку? Зв\'яжіться з нами безпосередньо на вашій улюбленій платформі для обміну повідомленнями.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Напишіть нам',
      telegram: 'Telegram',
      telegramSub: 'Напишіть нам',
      availability: 'Доступно понеділок – п\'ятниця, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Почніть відстеження сьогодні',
      description: 'Відкрийте EasyDrive у браузері та почніть передавати своє місцезнаходження в реальному часі на сервер Traccar менш ніж за 2 хвилини.',
      openApp: 'Відкрити EasyDrive',
      contactSales: 'Зв\'язатися з відділом продажів',
    },
    footer: {
      description: 'Персональний GPS-трекер, який передає ваше місцезнаходження кожні 10 секунд на ваш сервер Traccar OsmAnd.',
      quickLinks: 'Швидкі посилання',
      links: { openApp: 'Відкрити додаток', features: 'Функції', howItWorks: 'Як це працює', contact: 'Контакт' },
      contactTitle: 'Контакт',
      copyright: 'EasyDrive GPS-трекер. Усі права захищені.',
      serviceOnline: 'Сервіс онлайн',
    },
  },
  zh: {
    nav: { features: '功能', howItWorks: '使用方法', contact: '联系我们', openApp: '打开应用' },
    hero: {
      badge: '实时GPS追踪 — 网页应用',
      tagline: 'GPS追踪器',
      subtitle: '实时位置发送至Traccar',
      description: '个人GPS追踪器，每10秒将您的实时位置传输到您的Traccar OsmAnd服务器。实时启动、追踪并确认传输。',
      openWebApp: '打开网页应用',
      learnMore: '了解更多',
      stats: { updateInterval: '更新间隔', webBased: '网页应用', liveTracking: '实时追踪' },
      scroll: '滚动',
    },
    preview: { title: '查看实际效果', subtitle: '为驾驶员设计的简洁直观界面' },
    features: {
      badge: '功能',
      title: '您所需要的一切',
      subtitle: '为驾驶员、车队管理员以及所有需要可靠实时位置共享的人提供完整的GPS追踪解决方案。',
      items: {
        liveGps: { title: '实时GPS追踪', description: '每10秒将您的精确位置实时传输到您的Traccar OsmAnd服务器。' },
        webApp: { title: '网页应用 — 无需安装', description: '直接在浏览器中运行。无需从应用商店下载。打开即可立即开始追踪。' },
        traccar: { title: '兼容Traccar', description: '与您现有的Traccar / OsmAnd服务器无缝集成。只需输入服务器URL和设备ID。' },
        updates: { title: '10秒更新', description: '每10秒超频繁的位置更新确保您始终拥有最新的位置数据。' },
        profiles: { title: '驾驶员档案', description: '使用您的账户登录，创建命名的驾驶员档案，或仅使用设备ID匿名追踪。' },
        radio: { title: '在线广播', description: '内置在线广播播放器，在长途驾驶中为您提供娱乐 — 一个应用搞定一切。' },
      },
    },
    howItWorks: {
      badge: '使用方法',
      title: '几分钟内即可运行',
      subtitle: '无需安装，无需应用商店。只需打开浏览器即可开始追踪。',
      steps: {
        open: { title: '打开网页应用', description: '在手机或电脑上的任何现代浏览器中访问navigacija.easydrive.app。无需安装。' },
        profile: { title: '设置您的档案', description: '登录您的账户，创建新的驾驶员档案，或匿名继续。您的选择。' },
        server: { title: '输入服务器详情', description: '输入您的Traccar OsmAnd服务器URL和设备ID。应用会记住您的设置。' },
        track: { title: '开始追踪', description: '点击LIVE按钮，您的位置开始每10秒传输一次。实时确认传输。' },
      },
      tryNow: '立即试用 — 免费',
    },
    webOnly: {
      badge: '纯网页应用',
      title: '无需应用商店。',
      titleHighlight: '直接打开并追踪。',
      description: 'EasyDrive是一款渐进式网页应用（PWA）— 完全在您的浏览器中运行。无需下载，无需管理更新，不占用存储空间。',
      benefits: [
        '在任何配备现代浏览器的设备上运行',
        '无需Google Play或App Store',
        '始终保持最新 — 无需手动更新',
        '添加到主屏幕获得类似应用的体验',
        '通过service worker实现后台追踪',
      ],
      cards: {
        browser: { title: '基于浏览器', desc: '在Chrome、Safari、Firefox中打开 — 任何浏览器均可' },
        mobile: { title: '移动端就绪', desc: '针对智能手机优化，具有触控友好的界面' },
        instant: { title: '即时访问', desc: '无需等待下载或安装' },
        secure: { title: '安全', desc: 'HTTPS加密，您的数据保持私密' },
      },
    },
    traccarSection: {
      title: '兼容Traccar和OsmAnd',
      description: '已经在运行Traccar服务器？EasyDrive使用OsmAnd协议直接连接到您现有的基础设施。只需输入服务器URL和设备ID。',
      tags: { osmand: 'OsmAnd协议', traccar: 'Traccar服务器', realtime: '实时更新' },
    },
    contact: {
      badge: '联系我们',
      title: '取得联系',
      description: '对EasyDrive有疑问？想购买许可证或获得支持？直接在您喜欢的消息平台上联系我们。',
      whatsapp: 'WhatsApp',
      whatsappSub: '与我们聊天',
      telegram: 'Telegram',
      telegramSub: '给我们发消息',
      availability: '周一至周五 8:00 – 18:00 CET 可用',
    },
    cta: {
      title: '今天开始追踪',
      description: '在浏览器中打开EasyDrive，在不到2分钟内开始将您的实时位置传输到Traccar服务器。',
      openApp: '打开EasyDrive',
      contactSales: '联系销售',
    },
    footer: {
      description: '个人GPS追踪器，每10秒将您的实时位置传输到您的Traccar OsmAnd服务器。',
      quickLinks: '快速链接',
      links: { openApp: '打开应用', features: '功能', howItWorks: '使用方法', contact: '联系我们' },
      contactTitle: '联系方式',
      copyright: 'EasyDrive GPS追踪器。保留所有权利。',
      serviceOnline: '服务在线',
    },
  },
  ja: {
    nav: { features: '機能', howItWorks: '使い方', contact: 'お問い合わせ', openApp: 'アプリを開く' },
    hero: {
      badge: 'リアルタイムGPS追跡 — ウェブアプリ',
      tagline: 'GPSトラッカー',
      subtitle: 'リアルタイム位置情報をTraccarへ',
      description: '個人用GPSトラッカーで、10秒ごとにリアルタイムの位置情報をTraccar OsmAnd サーバーに送信します。リアルタイムで追跡を開始、確認できます。',
      openWebApp: 'ウェブアプリを開く',
      learnMore: '詳細を見る',
      stats: { updateInterval: '更新間隔', webBased: 'ウェブベース', liveTracking: 'ライブ追跡' },
      scroll: 'スクロール',
    },
    preview: { title: '実際の動作を見る', subtitle: 'ドライバー向けに設計されたクリーンで直感的なインターフェース' },
    features: {
      badge: '機能',
      title: '必要なすべてが揃っています',
      subtitle: 'ドライバー、フリートマネージャー、信頼性の高いリアルタイム位置共有が必要なすべての人向けの完全なGPS追跡ソリューション。',
      items: {
        liveGps: { title: 'リアルタイムGPS追跡', description: '10秒ごとに正確な位置情報をTraccar OsmAnd サーバーにリアルタイムで送信します。' },
        webApp: { title: 'ウェブアプリ — インストール不要', description: 'ブラウザで直接動作します。アプリストアからのダウンロードは不要です。開いてすぐに追跡を開始できます。' },
        traccar: { title: 'Traccar対応', description: '既存のTraccar / OsmAnd サーバーとシームレスに統合します。サーバーURLとデバイスIDを入力するだけです。' },
        updates: { title: '10秒ごとの更新', description: '10秒ごとの超高頻度の位置更新により、常に最新の位置データを確保できます。' },
        profiles: { title: 'ドライバープロフィール', description: 'アカウントでサインイン、名前付きドライバープロフィールを作成、またはデバイスIDのみで匿名追跡が可能です。' },
        radio: { title: 'オンラインラジオ', description: '長距離ドライブ中に楽しめる内蔵オンラインラジオプレーヤー — すべてが1つのアプリに。' },
      },
    },
    howItWorks: {
      badge: '使い方',
      title: '数分で使い始められます',
      subtitle: 'インストール不要、アプリストア不要。ブラウザを開いて追跡を開始するだけです。',
      steps: {
        open: { title: 'ウェブアプリを開く', description: 'スマートフォンまたはコンピューターの任意のモダンブラウザでnavigacija.easydrive.appにアクセスします。インストール不要。' },
        profile: { title: 'プロフィールを設定する', description: 'アカウントにサインイン、新しいドライバープロフィールを作成、または匿名で続行します。あなたの選択です。' },
        server: { title: 'サーバー詳細を入力する', description: 'Traccar OsmAnd サーバーのURLとデバイスIDを入力します。アプリは次回のために設定を記憶します。' },
        track: { title: '追跡を開始する', description: 'LIVEボタンを押すと、10秒ごとに位置情報の送信が開始されます。リアルタイムで送信を確認できます。' },
      },
      tryNow: '今すぐ試す — 無料',
    },
    webOnly: {
      badge: 'ウェブ専用アプリ',
      title: 'アプリストア不要。',
      titleHighlight: '開いて追跡するだけ。',
      description: 'EasyDriveはプログレッシブウェブアプリ（PWA）— ブラウザ内で完全に動作します。ダウンロード不要、アップデート管理不要、ストレージ使用なし。',
      benefits: [
        'モダンブラウザを搭載した任意のデバイスで動作',
        'Google PlayやApp Storeは不要',
        '常に最新 — 手動アップデート不要',
        'ホーム画面に追加してアプリのような体験',
        'サービスワーカーによるバックグラウンド追跡',
      ],
      cards: {
        browser: { title: 'ブラウザベース', desc: 'Chrome、Safari、Firefoxで開く — どのブラウザでも動作' },
        mobile: { title: 'モバイル対応', desc: 'タッチフレンドリーなUIでスマートフォン向けに最適化' },
        instant: { title: '即時アクセス', desc: 'ダウンロードやインストールの待機なし' },
        secure: { title: 'セキュア', desc: 'HTTPS暗号化、データはプライベートに保たれます' },
      },
    },
    traccarSection: {
      title: 'TraccarとOsmAnd対応',
      description: 'すでにTraccarサーバーを運用していますか？EasyDriveはOsmAnd プロトコルを使用して既存のインフラに直接接続します。サーバーURLとデバイスIDを入力するだけです。',
      tags: { osmand: 'OsmAnd プロトコル', traccar: 'Traccar サーバー', realtime: 'リアルタイム更新' },
    },
    contact: {
      badge: 'お問い合わせ',
      title: 'ご連絡ください',
      description: 'EasyDriveについてご質問がありますか？ライセンスの購入やサポートをご希望ですか？お好みのメッセージングプラットフォームで直接ご連絡ください。',
      whatsapp: 'WhatsApp',
      whatsappSub: 'チャットする',
      telegram: 'Telegram',
      telegramSub: 'メッセージを送る',
      availability: '月曜日〜金曜日 8:00〜18:00 CET 対応',
    },
    cta: {
      title: '今日から追跡を始めましょう',
      description: 'ブラウザでEasyDriveを開き、2分以内にTraccarサーバーへのリアルタイム位置情報の送信を開始します。',
      openApp: 'EasyDriveを開く',
      contactSales: '営業に問い合わせる',
    },
    footer: {
      description: '個人用GPSトラッカーで、10秒ごとにリアルタイムの位置情報をTraccar OsmAnd サーバーに送信します。',
      quickLinks: 'クイックリンク',
      links: { openApp: 'アプリを開く', features: '機能', howItWorks: '使い方', contact: 'お問い合わせ' },
      contactTitle: 'お問い合わせ',
      copyright: 'EasyDrive GPSトラッカー。全著作権所有。',
      serviceOnline: 'サービス稼働中',
    },
  },
  ar: {
    nav: { features: 'الميزات', howItWorks: 'كيف يعمل', contact: 'اتصل بنا', openApp: 'فتح التطبيق' },
    hero: {
      badge: 'تتبع GPS المباشر — تطبيق ويب',
      tagline: 'متتبع GPS',
      subtitle: 'الموقع المباشر إلى Traccar',
      description: 'متتبع GPS شخصي يرسل موقعك المباشر كل 10 ثوانٍ إلى خادم Traccar OsmAnd الخاص بك. ابدأ وتتبع وأكد الإرسال في الوقت الفعلي.',
      openWebApp: 'فتح تطبيق الويب',
      learnMore: 'اعرف المزيد',
      stats: { updateInterval: 'فترة التحديث', webBased: 'تطبيق ويب', liveTracking: 'التتبع المباشر' },
      scroll: 'مرر',
    },
    preview: { title: 'شاهده في العمل', subtitle: 'واجهة نظيفة وبديهية مصممة للسائقين' },
    features: {
      badge: 'الميزات',
      title: 'كل ما تحتاجه',
      subtitle: 'حل تتبع GPS كامل مصمم للسائقين ومديري الأساطيل وكل من يحتاج إلى مشاركة موقع موثوقة في الوقت الفعلي.',
      items: {
        liveGps: { title: 'تتبع GPS المباشر', description: 'يرسل موقعك الدقيق كل 10 ثوانٍ إلى خادم Traccar OsmAnd الخاص بك في الوقت الفعلي.' },
        webApp: { title: 'تطبيق ويب — بدون تثبيت', description: 'يعمل مباشرة في متصفحك. لا حاجة للتنزيل من متجر التطبيقات. افتح وابدأ التتبع فوراً.' },
        traccar: { title: 'متوافق مع Traccar', description: 'يتكامل بسلاسة مع خادم Traccar / OsmAnd الحالي. فقط أدخل عنوان URL للخادم ومعرف الجهاز.' },
        updates: { title: 'تحديثات كل 10 ثوانٍ', description: 'تحديثات الموقع الفائقة التكرار كل 10 ثوانٍ تضمن حصولك دائماً على أحدث بيانات الموقع.' },
        profiles: { title: 'ملفات السائقين', description: 'سجّل الدخول بحسابك أو أنشئ ملف سائق مسمى أو تتبع بشكل مجهول باستخدام معرف جهازك فقط.' },
        radio: { title: 'راديو عبر الإنترنت', description: 'مشغل راديو مدمج عبر الإنترنت لإبقائك مسلياً خلال الرحلات الطويلة — كل شيء في تطبيق واحد.' },
      },
    },
    howItWorks: {
      badge: 'كيف يعمل',
      title: 'جاهز للعمل في دقائق',
      subtitle: 'بدون تثبيت، بدون متجر تطبيقات. فقط افتح متصفحك وابدأ التتبع.',
      steps: {
        open: { title: 'افتح تطبيق الويب', description: 'انتقل إلى navigacija.easydrive.app في أي متصفح حديث على هاتفك أو جهاز الكمبيوتر. لا يلزم التثبيت.' },
        profile: { title: 'إعداد ملفك الشخصي', description: 'سجّل الدخول إلى حسابك أو أنشئ ملف سائق جديداً أو استمر بشكل مجهول. اختيارك.' },
        server: { title: 'أدخل تفاصيل الخادم', description: 'أدخل عنوان URL لخادم Traccar OsmAnd ومعرف الجهاز. يتذكر التطبيق إعداداتك للمرة القادمة.' },
        track: { title: 'ابدأ التتبع', description: 'اضغط زر LIVE وسيبدأ موقعك في الإرسال كل 10 ثوانٍ. أكد الإرسال في الوقت الفعلي.' },
      },
      tryNow: 'جربه الآن — مجاناً',
    },
    webOnly: {
      badge: 'تطبيق ويب فقط',
      title: 'بدون متجر تطبيقات.',
      titleHighlight: 'فقط افتح وتتبع.',
      description: 'EasyDrive هو تطبيق ويب تقدمي (PWA) — يعمل بالكامل في متصفحك. بدون تنزيلات، بدون إدارة تحديثات، بدون استخدام مساحة التخزين.',
      benefits: [
        'يعمل على أي جهاز بمتصفح حديث',
        'لا حاجة لـ Google Play أو App Store',
        'دائماً محدّث — بدون تحديثات يدوية',
        'أضفه إلى الشاشة الرئيسية للحصول على تجربة مثل التطبيق',
        'التتبع في الخلفية مع service worker',
      ],
      cards: {
        browser: { title: 'يعتمد على المتصفح', desc: 'افتح في Chrome أو Safari أو Firefox — أي متصفح يعمل' },
        mobile: { title: 'جاهز للجوال', desc: 'محسّن للهواتف الذكية بواجهة مستخدم ملائمة للمس' },
        instant: { title: 'وصول فوري', desc: 'بدون انتظار للتنزيلات أو التثبيت' },
        secure: { title: 'آمن', desc: 'مشفر بـ HTTPS، بياناتك تبقى خاصة' },
      },
    },
    traccarSection: {
      title: 'متوافق مع Traccar و OsmAnd',
      description: 'هل تشغّل بالفعل خادم Traccar؟ يتصل EasyDrive مباشرة ببنيتك التحتية الحالية باستخدام بروتوكول OsmAnd. فقط أدخل عنوان URL للخادم ومعرف الجهاز.',
      tags: { osmand: 'بروتوكول OsmAnd', traccar: 'خادم Traccar', realtime: 'تحديثات في الوقت الفعلي' },
    },
    contact: {
      badge: 'اتصل بنا',
      title: 'تواصل معنا',
      description: 'هل لديك أسئلة حول EasyDrive؟ تريد شراء ترخيص أو الحصول على دعم؟ تواصل معنا مباشرة على منصة المراسلة المفضلة لديك.',
      whatsapp: 'واتساب',
      whatsappSub: 'تحدث معنا',
      telegram: 'تيليغرام',
      telegramSub: 'راسلنا',
      availability: 'متاح الاثنين – الجمعة، 8:00 – 18:00 CET',
    },
    cta: {
      title: 'ابدأ التتبع اليوم',
      description: 'افتح EasyDrive في متصفحك وابدأ إرسال موقعك المباشر إلى خادم Traccar في أقل من دقيقتين.',
      openApp: 'فتح EasyDrive',
      contactSales: 'تواصل مع المبيعات',
    },
    footer: {
      description: 'متتبع GPS شخصي يرسل موقعك المباشر كل 10 ثوانٍ إلى خادم Traccar OsmAnd الخاص بك.',
      quickLinks: 'روابط سريعة',
      links: { openApp: 'فتح التطبيق', features: 'الميزات', howItWorks: 'كيف يعمل', contact: 'اتصل بنا' },
      contactTitle: 'اتصل بنا',
      copyright: 'EasyDrive متتبع GPS. جميع الحقوق محفوظة.',
      serviceOnline: 'الخدمة متصلة',
    },
  },
  es: {
    nav: { features: 'Características', howItWorks: 'Cómo funciona', contact: 'Contacto', openApp: 'Abrir app' },
    hero: {
      badge: 'Seguimiento GPS en vivo — Aplicación web',
      tagline: 'Rastreador GPS',
      subtitle: 'Ubicación en vivo a Traccar',
      description: 'Rastreador GPS personal que transmite tu ubicación en vivo cada 10 segundos a tu servidor Traccar OsmAnd. Inicia, rastrea y confirma transmisiones en tiempo real.',
      openWebApp: 'Abrir aplicación web',
      learnMore: 'Saber más',
      stats: { updateInterval: 'Intervalo de actualización', webBased: 'Basado en web', liveTracking: 'Seguimiento en vivo' },
      scroll: 'Desplazar',
    },
    preview: { title: 'Véalo en acción', subtitle: 'Interfaz limpia e intuitiva diseñada para conductores' },
    features: {
      badge: 'Características',
      title: 'Todo lo que necesitas',
      subtitle: 'Una solución completa de seguimiento GPS para conductores, gestores de flotas y cualquiera que necesite compartir ubicación en tiempo real de forma fiable.',
      items: {
        liveGps: { title: 'Seguimiento GPS en vivo', description: 'Transmite tu ubicación precisa cada 10 segundos a tu servidor Traccar OsmAnd en tiempo real.' },
        webApp: { title: 'Aplicación web — sin instalación', description: 'Funciona directamente en tu navegador. No se necesita descarga de la tienda de aplicaciones. Abre y empieza a rastrear al instante.' },
        traccar: { title: 'Compatible con Traccar', description: 'Se integra perfectamente con tu servidor Traccar / OsmAnd existente. Solo introduce la URL del servidor y el ID del dispositivo.' },
        updates: { title: 'Actualizaciones cada 10 segundos', description: 'Las actualizaciones de posición ultra-frecuentes cada 10 segundos garantizan que siempre tengas los datos de ubicación más actuales.' },
        profiles: { title: 'Perfiles de conductor', description: 'Inicia sesión con tu cuenta, crea un perfil de conductor con nombre o rastrea de forma anónima usando solo el ID de tu dispositivo.' },
        radio: { title: 'Radio en línea', description: 'Reproductor de radio en línea integrado para entretenerte durante los viajes largos — todo en una sola app.' },
      },
    },
    howItWorks: {
      badge: 'Cómo funciona',
      title: 'En funcionamiento en minutos',
      subtitle: 'Sin instalación, sin tienda de aplicaciones. Solo abre tu navegador y empieza a rastrear.',
      steps: {
        open: { title: 'Abre la aplicación web', description: 'Navega a navigacija.easydrive.app en cualquier navegador moderno en tu teléfono o computadora. No se requiere instalación.' },
        profile: { title: 'Configura tu perfil', description: 'Inicia sesión en tu cuenta, crea un nuevo perfil de conductor o continúa de forma anónima. Tu elección.' },
        server: { title: 'Introduce los detalles del servidor', description: 'Introduce la URL de tu servidor Traccar OsmAnd y el ID del dispositivo. La app recuerda tu configuración.' },
        track: { title: 'Empieza a rastrear', description: 'Pulsa el botón LIVE y tu ubicación empieza a transmitirse cada 10 segundos. Confirma las transmisiones en tiempo real.' },
      },
      tryNow: 'Pruébalo ahora — es gratis',
    },
    webOnly: {
      badge: 'Solo aplicación web',
      title: 'Sin tienda de aplicaciones.',
      titleHighlight: 'Solo abre y rastrea.',
      description: 'EasyDrive es una Aplicación Web Progresiva (PWA) — funciona completamente en tu navegador. Sin descargas, sin gestionar actualizaciones, sin usar espacio de almacenamiento.',
      benefits: [
        'Funciona en cualquier dispositivo con un navegador moderno',
        'No se requiere Google Play ni App Store',
        'Siempre actualizado — sin actualizaciones manuales',
        'Añade a la pantalla de inicio para una experiencia similar a una app',
        'Seguimiento en segundo plano con service worker',
      ],
      cards: {
        browser: { title: 'Basado en navegador', desc: 'Abre en Chrome, Safari, Firefox — cualquier navegador funciona' },
        mobile: { title: 'Listo para móvil', desc: 'Optimizado para smartphones con interfaz táctil' },
        instant: { title: 'Acceso instantáneo', desc: 'Sin esperar descargas ni instalaciones' },
        secure: { title: 'Seguro', desc: 'Cifrado HTTPS, tus datos permanecen privados' },
      },
    },
    traccarSection: {
      title: 'Compatible con Traccar y OsmAnd',
      description: '¿Ya tienes un servidor Traccar? EasyDrive se conecta directamente a tu infraestructura existente usando el protocolo OsmAnd. Solo introduce la URL del servidor y el ID del dispositivo.',
      tags: { osmand: 'Protocolo OsmAnd', traccar: 'Servidor Traccar', realtime: 'Actualizaciones en tiempo real' },
    },
    contact: {
      badge: 'Contáctanos',
      title: 'Ponte en contacto',
      description: '¿Tienes preguntas sobre EasyDrive? ¿Quieres comprar una licencia u obtener soporte? Contáctanos directamente en tu plataforma de mensajería preferida.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Chatea con nosotros',
      telegram: 'Telegram',
      telegramSub: 'Envíanos un mensaje',
      availability: 'Disponible lunes – viernes, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Empieza a rastrear hoy',
      description: 'Abre EasyDrive en tu navegador y empieza a transmitir tu ubicación en vivo a tu servidor Traccar en menos de 2 minutos.',
      openApp: 'Abrir EasyDrive',
      contactSales: 'Contactar ventas',
    },
    footer: {
      description: 'Rastreador GPS personal que transmite tu ubicación en vivo cada 10 segundos a tu servidor Traccar OsmAnd.',
      quickLinks: 'Enlaces rápidos',
      links: { openApp: 'Abrir app', features: 'Características', howItWorks: 'Cómo funciona', contact: 'Contacto' },
      contactTitle: 'Contacto',
      copyright: 'EasyDrive Rastreador GPS. Todos los derechos reservados.',
      serviceOnline: 'Servicio en línea',
    },
  },
  fr: {
    nav: { features: 'Fonctionnalités', howItWorks: 'Comment ça marche', contact: 'Contact', openApp: "Ouvrir l'app" },
    hero: {
      badge: 'Suivi GPS en direct — Application web',
      tagline: 'Traceur GPS',
      subtitle: 'Position en direct vers Traccar',
      description: 'Traceur GPS personnel qui transmet votre position en direct toutes les 10 secondes à votre serveur Traccar OsmAnd. Démarrez, suivez et confirmez les transmissions en temps réel.',
      openWebApp: "Ouvrir l'application web",
      learnMore: 'En savoir plus',
      stats: { updateInterval: "Intervalle de mise à jour", webBased: 'Application web', liveTracking: 'Suivi en direct' },
      scroll: 'Défiler',
    },
    preview: { title: 'Voir en action', subtitle: 'Interface propre et intuitive conçue pour les conducteurs' },
    features: {
      badge: 'Fonctionnalités',
      title: 'Tout ce dont vous avez besoin',
      subtitle: 'Une solution complète de suivi GPS pour les conducteurs, les gestionnaires de flotte et tous ceux qui ont besoin de partage de position fiable en temps réel.',
      items: {
        liveGps: { title: 'Suivi GPS en direct', description: 'Transmet votre position précise toutes les 10 secondes à votre serveur Traccar OsmAnd en temps réel.' },
        webApp: { title: 'Application web — sans installation', description: 'Fonctionne directement dans votre navigateur. Pas de téléchargement depuis la boutique d\'applications. Ouvrez et commencez à suivre instantanément.' },
        traccar: { title: 'Compatible Traccar', description: 'S\'intègre parfaitement à votre serveur Traccar / OsmAnd existant. Entrez simplement l\'URL de votre serveur et l\'ID de l\'appareil.' },
        updates: { title: 'Mises à jour toutes les 10 secondes', description: 'Des mises à jour de position ultra-fréquentes toutes les 10 secondes garantissent que vous disposez toujours des données de localisation les plus récentes.' },
        profiles: { title: 'Profils de conducteur', description: 'Connectez-vous avec votre compte, créez un profil de conducteur nommé ou suivez anonymement en utilisant uniquement l\'ID de votre appareil.' },
        radio: { title: 'Radio en ligne', description: 'Lecteur radio en ligne intégré pour vous divertir lors des longs trajets — tout en une seule application.' },
      },
    },
    howItWorks: {
      badge: 'Comment ça marche',
      title: 'Opérationnel en quelques minutes',
      subtitle: 'Pas d\'installation, pas de boutique d\'applications. Ouvrez simplement votre navigateur et commencez à suivre.',
      steps: {
        open: { title: "Ouvrir l'application web", description: 'Accédez à navigacija.easydrive.app dans n\'importe quel navigateur moderne sur votre téléphone ou ordinateur. Aucune installation requise.' },
        profile: { title: 'Configurer votre profil', description: 'Connectez-vous à votre compte, créez un nouveau profil de conducteur ou continuez anonymement. Votre choix.' },
        server: { title: 'Entrer les détails du serveur', description: 'Saisissez l\'URL de votre serveur Traccar OsmAnd et l\'ID de l\'appareil. L\'application mémorise vos paramètres.' },
        track: { title: 'Commencer le suivi', description: 'Appuyez sur le bouton LIVE et votre position commence à se transmettre toutes les 10 secondes. Confirmez les transmissions en temps réel.' },
      },
      tryNow: 'Essayez maintenant — c\'est gratuit',
    },
    webOnly: {
      badge: 'Application web uniquement',
      title: 'Pas de boutique d\'applications.',
      titleHighlight: 'Ouvrez et suivez.',
      description: 'EasyDrive est une Application Web Progressive (PWA) — elle fonctionne entièrement dans votre navigateur. Pas de téléchargements, pas de mises à jour à gérer, pas d\'espace de stockage utilisé.',
      benefits: [
        'Fonctionne sur n\'importe quel appareil avec un navigateur moderne',
        'Google Play ou App Store non requis',
        'Toujours à jour — pas de mises à jour manuelles',
        'Ajoutez à l\'écran d\'accueil pour une expérience similaire à une application',
        'Suivi en arrière-plan avec service worker',
      ],
      cards: {
        browser: { title: 'Basé sur le navigateur', desc: 'Ouvrez dans Chrome, Safari, Firefox — n\'importe quel navigateur fonctionne' },
        mobile: { title: 'Prêt pour mobile', desc: 'Optimisé pour les smartphones avec une interface tactile' },
        instant: { title: 'Accès instantané', desc: 'Pas d\'attente pour les téléchargements ou les installations' },
        secure: { title: 'Sécurisé', desc: 'Chiffré HTTPS, vos données restent privées' },
      },
    },
    traccarSection: {
      title: 'Compatible Traccar et OsmAnd',
      description: 'Vous utilisez déjà un serveur Traccar ? EasyDrive se connecte directement à votre infrastructure existante en utilisant le protocole OsmAnd. Entrez simplement l\'URL de votre serveur et l\'ID de l\'appareil.',
      tags: { osmand: 'Protocole OsmAnd', traccar: 'Serveur Traccar', realtime: 'Mises à jour en temps réel' },
    },
    contact: {
      badge: 'Contactez-nous',
      title: 'Prenez contact',
      description: 'Des questions sur EasyDrive ? Vous souhaitez acheter une licence ou obtenir de l\'aide ? Contactez-nous directement sur votre plateforme de messagerie préférée.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Discutez avec nous',
      telegram: 'Telegram',
      telegramSub: 'Envoyez-nous un message',
      availability: 'Disponible lundi – vendredi, 8h00 – 18h00 CET',
    },
    cta: {
      title: 'Commencez à suivre aujourd\'hui',
      description: 'Ouvrez EasyDrive dans votre navigateur et commencez à transmettre votre position en direct à votre serveur Traccar en moins de 2 minutes.',
      openApp: 'Ouvrir EasyDrive',
      contactSales: 'Contacter les ventes',
    },
    footer: {
      description: 'Traceur GPS personnel qui transmet votre position en direct toutes les 10 secondes à votre serveur Traccar OsmAnd.',
      quickLinks: 'Liens rapides',
      links: { openApp: "Ouvrir l'app", features: 'Fonctionnalités', howItWorks: 'Comment ça marche', contact: 'Contact' },
      contactTitle: 'Contact',
      copyright: 'EasyDrive Traceur GPS. Tous droits réservés.',
      serviceOnline: 'Service en ligne',
    },
  },
  it: {
    nav: { features: 'Funzionalità', howItWorks: 'Come funziona', contact: 'Contatto', openApp: "Apri l'app" },
    hero: {
      badge: 'Tracciamento GPS in diretta — App web',
      tagline: 'Tracker GPS',
      subtitle: 'Posizione in diretta su Traccar',
      description: 'Tracker GPS personale che trasmette la tua posizione in diretta ogni 10 secondi al tuo server Traccar OsmAnd. Avvia, traccia e conferma le trasmissioni in tempo reale.',
      openWebApp: "Apri l'app web",
      learnMore: 'Scopri di più',
      stats: { updateInterval: 'Intervallo di aggiornamento', webBased: 'App web', liveTracking: 'Tracciamento in diretta' },
      scroll: 'Scorri',
    },
    preview: { title: 'Guarda in azione', subtitle: 'Interfaccia pulita e intuitiva progettata per i conducenti' },
    features: {
      badge: 'Funzionalità',
      title: 'Tutto ciò di cui hai bisogno',
      subtitle: 'Una soluzione completa di tracciamento GPS per conducenti, gestori di flotte e chiunque abbia bisogno di condivisione della posizione affidabile in tempo reale.',
      items: {
        liveGps: { title: 'Tracciamento GPS in diretta', description: 'Trasmette la tua posizione precisa ogni 10 secondi al tuo server Traccar OsmAnd in tempo reale.' },
        webApp: { title: 'App web — nessuna installazione', description: 'Funziona direttamente nel tuo browser. Nessun download dall\'app store necessario. Apri e inizia subito a tracciare.' },
        traccar: { title: 'Compatibile con Traccar', description: 'Si integra perfettamente con il tuo server Traccar / OsmAnd esistente. Inserisci semplicemente l\'URL del server e l\'ID del dispositivo.' },
        updates: { title: 'Aggiornamenti ogni 10 secondi', description: 'Gli aggiornamenti ultra-frequenti della posizione ogni 10 secondi garantiscono che tu abbia sempre i dati di posizione più aggiornati.' },
        profiles: { title: 'Profili conducente', description: 'Accedi con il tuo account, crea un profilo conducente con nome o traccia in modo anonimo usando solo l\'ID del dispositivo.' },
        radio: { title: 'Radio online', description: 'Lettore radio online integrato per intrattenerti durante i lunghi viaggi — tutto in un\'unica app.' },
      },
    },
    howItWorks: {
      badge: 'Come funziona',
      title: 'Operativo in pochi minuti',
      subtitle: 'Nessuna installazione, nessun app store. Apri semplicemente il browser e inizia a tracciare.',
      steps: {
        open: { title: "Apri l'app web", description: 'Vai su navigacija.easydrive.app in qualsiasi browser moderno sul tuo telefono o computer. Nessuna installazione richiesta.' },
        profile: { title: 'Configura il tuo profilo', description: 'Accedi al tuo account, crea un nuovo profilo conducente o continua in modo anonimo. La tua scelta.' },
        server: { title: 'Inserisci i dettagli del server', description: 'Inserisci l\'URL del tuo server Traccar OsmAnd e l\'ID del dispositivo. L\'app ricorda le tue impostazioni.' },
        track: { title: 'Inizia il tracciamento', description: 'Premi il pulsante LIVE e la tua posizione inizia a trasmettere ogni 10 secondi. Conferma le trasmissioni in tempo reale.' },
      },
      tryNow: 'Provalo ora — è gratuito',
    },
    webOnly: {
      badge: 'Solo app web',
      title: 'Nessun app store.',
      titleHighlight: 'Apri e traccia.',
      description: 'EasyDrive è una Progressive Web App (PWA) — funziona interamente nel tuo browser. Nessun download, nessun aggiornamento da gestire, nessuno spazio di archiviazione utilizzato.',
      benefits: [
        'Funziona su qualsiasi dispositivo con un browser moderno',
        'Google Play o App Store non richiesti',
        'Sempre aggiornato — nessun aggiornamento manuale',
        'Aggiungi alla schermata home per un\'esperienza simile a un\'app',
        'Tracciamento in background con service worker',
      ],
      cards: {
        browser: { title: 'Basato su browser', desc: 'Apri in Chrome, Safari, Firefox — qualsiasi browser funziona' },
        mobile: { title: 'Pronto per mobile', desc: 'Ottimizzato per smartphone con interfaccia touch-friendly' },
        instant: { title: 'Accesso immediato', desc: 'Nessuna attesa per download o installazioni' },
        secure: { title: 'Sicuro', desc: 'Crittografato HTTPS, i tuoi dati rimangono privati' },
      },
    },
    traccarSection: {
      title: 'Compatibile con Traccar e OsmAnd',
      description: 'Stai già usando un server Traccar? EasyDrive si connette direttamente alla tua infrastruttura esistente usando il protocollo OsmAnd. Inserisci semplicemente l\'URL del server e l\'ID del dispositivo.',
      tags: { osmand: 'Protocollo OsmAnd', traccar: 'Server Traccar', realtime: 'Aggiornamenti in tempo reale' },
    },
    contact: {
      badge: 'Contattaci',
      title: 'Mettiti in contatto',
      description: 'Hai domande su EasyDrive? Vuoi acquistare una licenza o ricevere supporto? Contattaci direttamente sulla tua piattaforma di messaggistica preferita.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Chatta con noi',
      telegram: 'Telegram',
      telegramSub: 'Scrivici',
      availability: 'Disponibile lunedì – venerdì, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Inizia a tracciare oggi',
      description: 'Apri EasyDrive nel tuo browser e inizia a trasmettere la tua posizione in diretta al tuo server Traccar in meno di 2 minuti.',
      openApp: 'Apri EasyDrive',
      contactSales: 'Contatta le vendite',
    },
    footer: {
      description: 'Tracker GPS personale che trasmette la tua posizione in diretta ogni 10 secondi al tuo server Traccar OsmAnd.',
      quickLinks: 'Link rapidi',
      links: { openApp: "Apri l'app", features: 'Funzionalità', howItWorks: 'Come funziona', contact: 'Contatto' },
      contactTitle: 'Contatto',
      copyright: 'EasyDrive Tracker GPS. Tutti i diritti riservati.',
      serviceOnline: 'Servizio online',
    },
  },
  ko: {
    nav: { features: '기능', howItWorks: '사용 방법', contact: '문의', openApp: '앱 열기' },
    hero: {
      badge: '실시간 GPS 추적 — 웹 앱',
      tagline: 'GPS 트래커',
      subtitle: 'Traccar로 실시간 위치 전송',
      description: '10초마다 실시간 위치를 Traccar OsmAnd 서버로 전송하는 개인 GPS 트래커입니다. 실시간으로 추적을 시작하고 전송을 확인하세요.',
      openWebApp: '웹 앱 열기',
      learnMore: '더 알아보기',
      stats: { updateInterval: '업데이트 간격', webBased: '웹 기반', liveTracking: '실시간 추적' },
      scroll: '스크롤',
    },
    preview: { title: '실제 동작 보기', subtitle: '운전자를 위해 설계된 깔끔하고 직관적인 인터페이스' },
    features: {
      badge: '기능',
      title: '필요한 모든 것',
      subtitle: '운전자, 차량 관리자, 신뢰할 수 있는 실시간 위치 공유가 필요한 모든 사람을 위한 완전한 GPS 추적 솔루션.',
      items: {
        liveGps: { title: '실시간 GPS 추적', description: '10초마다 정확한 위치를 Traccar OsmAnd 서버로 실시간 전송합니다.' },
        webApp: { title: '웹 앱 — 설치 불필요', description: '브라우저에서 직접 작동합니다. 앱 스토어 다운로드가 필요 없습니다. 열고 즉시 추적을 시작하세요.' },
        traccar: { title: 'Traccar 호환', description: '기존 Traccar / OsmAnd 서버와 원활하게 통합됩니다. 서버 URL과 기기 ID만 입력하면 됩니다.' },
        updates: { title: '10초 업데이트', description: '10초마다 초고빈도 위치 업데이트로 항상 최신 위치 데이터를 확보할 수 있습니다.' },
        profiles: { title: '운전자 프로필', description: '계정으로 로그인하거나 이름이 있는 운전자 프로필을 만들거나 기기 ID만으로 익명으로 추적하세요.' },
        radio: { title: '온라인 라디오', description: '장거리 운전 중 즐길 수 있는 내장 온라인 라디오 플레이어 — 모든 것이 하나의 앱에.' },
      },
    },
    howItWorks: {
      badge: '사용 방법',
      title: '몇 분 안에 시작',
      subtitle: '설치 불필요, 앱 스토어 불필요. 브라우저를 열고 추적을 시작하세요.',
      steps: {
        open: { title: '웹 앱 열기', description: '휴대폰이나 컴퓨터의 최신 브라우저에서 navigacija.easydrive.app으로 이동하세요. 설치가 필요 없습니다.' },
        profile: { title: '프로필 설정', description: '계정에 로그인하거나 새 운전자 프로필을 만들거나 익명으로 계속하세요. 당신의 선택입니다.' },
        server: { title: '서버 정보 입력', description: 'Traccar OsmAnd 서버 URL과 기기 ID를 입력하세요. 앱이 다음 번을 위해 설정을 기억합니다.' },
        track: { title: '추적 시작', description: 'LIVE 버튼을 누르면 10초마다 위치 전송이 시작됩니다. 실시간으로 전송을 확인하세요.' },
      },
      tryNow: '지금 사용해보기 — 무료',
    },
    webOnly: {
      badge: '웹 전용 앱',
      title: '앱 스토어 불필요.',
      titleHighlight: '열고 추적하기만 하면 됩니다.',
      description: 'EasyDrive는 프로그레시브 웹 앱(PWA)으로 브라우저에서 완전히 실행됩니다. 다운로드 없음, 업데이트 관리 없음, 저장 공간 사용 없음.',
      benefits: [
        '최신 브라우저가 있는 모든 기기에서 작동',
        'Google Play 또는 App Store 불필요',
        '항상 최신 상태 — 수동 업데이트 없음',
        '홈 화면에 추가하여 앱과 같은 경험',
        'service worker로 백그라운드 추적',
      ],
      cards: {
        browser: { title: '브라우저 기반', desc: 'Chrome, Safari, Firefox에서 열기 — 모든 브라우저 작동' },
        mobile: { title: '모바일 준비', desc: '터치 친화적 UI로 스마트폰에 최적화' },
        instant: { title: '즉시 접근', desc: '다운로드나 설치를 기다릴 필요 없음' },
        secure: { title: '안전', desc: 'HTTPS 암호화, 데이터는 비공개로 유지' },
      },
    },
    traccarSection: {
      title: 'Traccar 및 OsmAnd 호환',
      description: '이미 Traccar 서버를 운영 중이신가요? EasyDrive는 OsmAnd 프로토콜을 사용하여 기존 인프라에 직접 연결됩니다. 서버 URL과 기기 ID만 입력하면 됩니다.',
      tags: { osmand: 'OsmAnd 프로토콜', traccar: 'Traccar 서버', realtime: '실시간 업데이트' },
    },
    contact: {
      badge: '문의하기',
      title: '연락하기',
      description: 'EasyDrive에 대한 질문이 있으신가요? 라이선스를 구매하거나 지원을 받고 싶으신가요? 선호하는 메시징 플랫폼에서 직접 연락하세요.',
      whatsapp: 'WhatsApp',
      whatsappSub: '채팅하기',
      telegram: 'Telegram',
      telegramSub: '메시지 보내기',
      availability: '월요일 – 금요일 8:00 – 18:00 CET 이용 가능',
    },
    cta: {
      title: '오늘 추적 시작',
      description: '브라우저에서 EasyDrive를 열고 2분 이내에 Traccar 서버로 실시간 위치 전송을 시작하세요.',
      openApp: 'EasyDrive 열기',
      contactSales: '영업팀 문의',
    },
    footer: {
      description: '10초마다 실시간 위치를 Traccar OsmAnd 서버로 전송하는 개인 GPS 트래커.',
      quickLinks: '빠른 링크',
      links: { openApp: '앱 열기', features: '기능', howItWorks: '사용 방법', contact: '문의' },
      contactTitle: '문의',
      copyright: 'EasyDrive GPS 트래커. 모든 권리 보유.',
      serviceOnline: '서비스 온라인',
    },
  },
  sv: {
    nav: { features: 'Funktioner', howItWorks: 'Hur det fungerar', contact: 'Kontakt', openApp: 'Öppna appen' },
    hero: {
      badge: 'Live GPS-spårning — Webbapp',
      tagline: 'GPS-spårare',
      subtitle: 'Live-position till Traccar',
      description: 'Personlig GPS-spårare som sänder din live-position var 10:e sekund till din Traccar OsmAnd-server. Starta, spåra och bekräfta sändningar i realtid.',
      openWebApp: 'Öppna webbappen',
      learnMore: 'Läs mer',
      stats: { updateInterval: 'Uppdateringsintervall', webBased: 'Webbaserad', liveTracking: 'Live-spårning' },
      scroll: 'Scrolla',
    },
    preview: { title: 'Se det i aktion', subtitle: 'Rent, intuitivt gränssnitt designat för förare' },
    features: {
      badge: 'Funktioner',
      title: 'Allt du behöver',
      subtitle: 'En komplett GPS-spårningslösning för förare, flottchefer och alla som behöver pålitlig realtidsdelning av position.',
      items: {
        liveGps: { title: 'Live GPS-spårning', description: 'Sänder din exakta position var 10:e sekund till din Traccar OsmAnd-server i realtid.' },
        webApp: { title: 'Webbapp — ingen installation', description: 'Fungerar direkt i din webbläsare. Ingen nedladdning från appbutik behövs. Öppna och börja spåra direkt.' },
        traccar: { title: 'Traccar-kompatibel', description: 'Integreras sömlöst med din befintliga Traccar / OsmAnd-server. Ange bara din server-URL och enhets-ID.' },
        updates: { title: '10-sekunders uppdateringar', description: 'Ultra-frekventa positionsuppdateringar var 10:e sekund säkerställer att du alltid har de senaste positionsdata.' },
        profiles: { title: 'Förarprofiler', description: 'Logga in med ditt konto, skapa en namngiven förarprofil eller spåra anonymt med bara ditt enhets-ID.' },
        radio: { title: 'Online-radio', description: 'Inbyggd online-radiospelare för att hålla dig underhållen under långa körningar — allt i en app.' },
      },
    },
    howItWorks: {
      badge: 'Hur det fungerar',
      title: 'Igång på några minuter',
      subtitle: 'Ingen installation, ingen appbutik. Öppna bara din webbläsare och börja spåra.',
      steps: {
        open: { title: 'Öppna webbappen', description: 'Navigera till navigacija.easydrive.app i valfri modern webbläsare på din telefon eller dator. Ingen installation krävs.' },
        profile: { title: 'Ställ in din profil', description: 'Logga in på ditt konto, skapa en ny förarprofil eller fortsätt anonymt. Ditt val.' },
        server: { title: 'Ange serverdetaljer', description: 'Ange din Traccar OsmAnd-server-URL och enhets-ID. Appen kommer ihåg dina inställningar.' },
        track: { title: 'Börja spåra', description: 'Tryck på LIVE-knappen och din position börjar sändas var 10:e sekund. Bekräfta sändningar i realtid.' },
      },
      tryNow: 'Prova nu — det är gratis',
    },
    webOnly: {
      badge: 'Endast webbapp',
      title: 'Ingen appbutik.',
      titleHighlight: 'Öppna och spåra.',
      description: 'EasyDrive är en Progressive Web App (PWA) — den körs helt i din webbläsare. Inga nedladdningar, inga uppdateringar att hantera, inget lagringsutrymme används.',
      benefits: [
        'Fungerar på vilken enhet som helst med en modern webbläsare',
        'Google Play eller App Store krävs inte',
        'Alltid uppdaterad — inga manuella uppdateringar',
        'Lägg till på hemskärmen för en appliknande upplevelse',
        'Bakgrundsspårning med service worker',
      ],
      cards: {
        browser: { title: 'Webbläsarbaserad', desc: 'Öppna i Chrome, Safari, Firefox — vilken webbläsare som helst fungerar' },
        mobile: { title: 'Mobilklar', desc: 'Optimerad för smartphones med pekskärmsvänligt gränssnitt' },
        instant: { title: 'Omedelbar åtkomst', desc: 'Inget väntande på nedladdningar eller installationer' },
        secure: { title: 'Säker', desc: 'HTTPS-krypterad, dina data förblir privata' },
      },
    },
    traccarSection: {
      title: 'Traccar och OsmAnd-kompatibel',
      description: 'Kör du redan en Traccar-server? EasyDrive ansluter direkt till din befintliga infrastruktur med OsmAnd-protokollet. Ange bara din server-URL och enhets-ID.',
      tags: { osmand: 'OsmAnd-protokoll', traccar: 'Traccar-server', realtime: 'Realtidsuppdateringar' },
    },
    contact: {
      badge: 'Kontakta oss',
      title: 'Ta kontakt',
      description: 'Har du frågor om EasyDrive? Vill du köpa en licens eller få support? Kontakta oss direkt på din föredragna meddelandeplattform.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Chatta med oss',
      telegram: 'Telegram',
      telegramSub: 'Skicka meddelande',
      availability: 'Tillgänglig måndag – fredag, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Börja spåra idag',
      description: 'Öppna EasyDrive i din webbläsare och börja sända din live-position till din Traccar-server på under 2 minuter.',
      openApp: 'Öppna EasyDrive',
      contactSales: 'Kontakta försäljning',
    },
    footer: {
      description: 'Personlig GPS-spårare som sänder din live-position var 10:e sekund till din Traccar OsmAnd-server.',
      quickLinks: 'Snabblänkar',
      links: { openApp: 'Öppna appen', features: 'Funktioner', howItWorks: 'Hur det fungerar', contact: 'Kontakt' },
      contactTitle: 'Kontakt',
      copyright: 'EasyDrive GPS-spårare. Alla rättigheter förbehållna.',
      serviceOnline: 'Tjänst online',
    },
  },
  no: {
    nav: { features: 'Funksjoner', howItWorks: 'Slik fungerer det', contact: 'Kontakt', openApp: 'Åpne appen' },
    hero: {
      badge: 'Live GPS-sporing — Nettapp',
      tagline: 'GPS-sporer',
      subtitle: 'Live-posisjon til Traccar',
      description: 'Personlig GPS-sporer som sender din live-posisjon hvert 10. sekund til din Traccar OsmAnd-server. Start, spor og bekreft sendinger i sanntid.',
      openWebApp: 'Åpne nettappen',
      learnMore: 'Lær mer',
      stats: { updateInterval: 'Oppdateringsintervall', webBased: 'Nettbasert', liveTracking: 'Live-sporing' },
      scroll: 'Rull',
    },
    preview: { title: 'Se det i aksjon', subtitle: 'Rent, intuitivt grensesnitt designet for sjåfører' },
    features: {
      badge: 'Funksjoner',
      title: 'Alt du trenger',
      subtitle: 'En komplett GPS-sporingslösning for sjåfører, flåteledere og alle som trenger pålitelig deling av posisjon i sanntid.',
      items: {
        liveGps: { title: 'Live GPS-sporing', description: 'Sender din nøyaktige posisjon hvert 10. sekund til din Traccar OsmAnd-server i sanntid.' },
        webApp: { title: 'Nettapp — ingen installasjon', description: 'Fungerer direkte i nettleseren din. Ingen nedlasting fra appbutikk nødvendig. Åpne og begynn å spore umiddelbart.' },
        traccar: { title: 'Traccar-kompatibel', description: 'Integreres sømløst med din eksisterende Traccar / OsmAnd-server. Bare skriv inn server-URL og enhets-ID.' },
        updates: { title: '10-sekunders oppdateringer', description: 'Ultra-hyppige posisjonsoppdateringer hvert 10. sekund sikrer at du alltid har de nyeste posisjonsdataene.' },
        profiles: { title: 'Sjåførprofiler', description: 'Logg inn med kontoen din, opprett en navngitt sjåførprofil, eller spor anonymt med bare enhets-ID.' },
        radio: { title: 'Nettradio', description: 'Innebygd nettradio-spiller for å holde deg underholdt under lange kjøreturer — alt i én app.' },
      },
    },
    howItWorks: {
      badge: 'Slik fungerer det',
      title: 'Klar på noen minutter',
      subtitle: 'Ingen installasjon, ingen appbutikk. Bare åpne nettleseren og begynn å spore.',
      steps: {
        open: { title: 'Åpne nettappen', description: 'Naviger til navigacija.easydrive.app i en hvilken som helst moderne nettleser på telefonen eller datamaskinen. Ingen installasjon nødvendig.' },
        profile: { title: 'Sett opp profilen din', description: 'Logg inn på kontoen din, opprett en ny sjåførprofil, eller fortsett anonymt. Ditt valg.' },
        server: { title: 'Skriv inn serverdetaljer', description: 'Skriv inn Traccar OsmAnd-server-URL og enhets-ID. Appen husker innstillingene dine.' },
        track: { title: 'Begynn å spore', description: 'Trykk på LIVE-knappen og posisjonen din begynner å sende hvert 10. sekund. Bekreft sendinger i sanntid.' },
      },
      tryNow: 'Prøv det nå — det er gratis',
    },
    webOnly: {
      badge: 'Kun nettapp',
      title: 'Ingen appbutikk.',
      titleHighlight: 'Bare åpne og spor.',
      description: 'EasyDrive er en Progressive Web App (PWA) — den kjører helt i nettleseren din. Ingen nedlastinger, ingen oppdateringer å håndtere, ingen lagringsplass brukt.',
      benefits: [
        'Fungerer på hvilken som helst enhet med en moderne nettleser',
        'Google Play eller App Store ikke nødvendig',
        'Alltid oppdatert — ingen manuelle oppdateringer',
        'Legg til på startskjermen for en app-lignende opplevelse',
        'Bakgrunnssporing med service worker',
      ],
      cards: {
        browser: { title: 'Nettleserbasert', desc: 'Åpne i Chrome, Safari, Firefox — hvilken som helst nettleser fungerer' },
        mobile: { title: 'Mobilklar', desc: 'Optimalisert for smarttelefoner med berøringsvennlig grensesnitt' },
        instant: { title: 'Umiddelbar tilgang', desc: 'Ingen venting på nedlastinger eller installasjoner' },
        secure: { title: 'Sikker', desc: 'HTTPS-kryptert, dataene dine forblir private' },
      },
    },
    traccarSection: {
      title: 'Traccar og OsmAnd-kompatibel',
      description: 'Kjører du allerede en Traccar-server? EasyDrive kobler seg direkte til din eksisterende infrastruktur ved hjelp av OsmAnd-protokollen. Bare skriv inn server-URL og enhets-ID.',
      tags: { osmand: 'OsmAnd-protokoll', traccar: 'Traccar-server', realtime: 'Sanntidsoppdateringer' },
    },
    contact: {
      badge: 'Kontakt oss',
      title: 'Ta kontakt',
      description: 'Har du spørsmål om EasyDrive? Vil du kjøpe en lisens eller få støtte? Kontakt oss direkte på din foretrukne meldingsplattform.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Chat med oss',
      telegram: 'Telegram',
      telegramSub: 'Send melding',
      availability: 'Tilgjengelig mandag – fredag, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Begynn å spore i dag',
      description: 'Åpne EasyDrive i nettleseren og begynn å sende din live-posisjon til Traccar-serveren på under 2 minutter.',
      openApp: 'Åpne EasyDrive',
      contactSales: 'Kontakt salg',
    },
    footer: {
      description: 'Personlig GPS-sporer som sender din live-posisjon hvert 10. sekund til din Traccar OsmAnd-server.',
      quickLinks: 'Hurtiglenker',
      links: { openApp: 'Åpne appen', features: 'Funksjoner', howItWorks: 'Slik fungerer det', contact: 'Kontakt' },
      contactTitle: 'Kontakt',
      copyright: 'EasyDrive GPS-sporer. Alle rettigheter forbeholdt.',
      serviceOnline: 'Tjeneste online',
    },
  },
  fi: {
    nav: { features: 'Ominaisuudet', howItWorks: 'Miten se toimii', contact: 'Yhteystiedot', openApp: 'Avaa sovellus' },
    hero: {
      badge: 'Live GPS-seuranta — Verkkosovellus',
      tagline: 'GPS-seuranta',
      subtitle: 'Reaaliaikainen sijainti Traccariin',
      description: 'Henkilökohtainen GPS-seuranta, joka lähettää sijaintisi reaaliajassa 10 sekunnin välein Traccar OsmAnd -palvelimellesi. Aloita, seuraa ja vahvista lähetykset reaaliajassa.',
      openWebApp: 'Avaa verkkosovellus',
      learnMore: 'Lue lisää',
      stats: { updateInterval: 'Päivitysväli', webBased: 'Verkkopohjainen', liveTracking: 'Reaaliaikainen seuranta' },
      scroll: 'Vieritä',
    },
    preview: { title: 'Katso toiminnassa', subtitle: 'Selkeä, intuitiivinen käyttöliittymä kuljettajille' },
    features: {
      badge: 'Ominaisuudet',
      title: 'Kaikki mitä tarvitset',
      subtitle: 'Täydellinen GPS-seurantaratkaisu kuljettajille, laivastopäälliköille ja kaikille, jotka tarvitsevat luotettavaa reaaliaikaista sijainninjakoa.',
      items: {
        liveGps: { title: 'Live GPS-seuranta', description: 'Lähettää tarkan sijaintisi 10 sekunnin välein Traccar OsmAnd -palvelimellesi reaaliajassa.' },
        webApp: { title: 'Verkkosovellus — ei asennusta', description: 'Toimii suoraan selaimessasi. Sovelluskaupoista lataaminen ei ole tarpeen. Avaa ja aloita seuranta välittömästi.' },
        traccar: { title: 'Traccar-yhteensopiva', description: 'Integroituu saumattomasti olemassa olevaan Traccar / OsmAnd -palvelimeesi. Syötä vain palvelimen URL ja laitetunnus.' },
        updates: { title: '10 sekunnin päivitykset', description: 'Erittäin tiheät sijaintipäivitykset 10 sekunnin välein varmistavat, että sinulla on aina ajantasaisimmat sijaintitiedot.' },
        profiles: { title: 'Kuljettajaprofiilit', description: 'Kirjaudu sisään tilillesi, luo nimetty kuljettajaprofiili tai seuraa anonyymisti pelkällä laitetunnuksella.' },
        radio: { title: 'Verkkoradio', description: 'Sisäänrakennettu verkkoradisoitin pitää sinut viihdytettynä pitkillä ajoilla — kaikki yhdessä sovelluksessa.' },
      },
    },
    howItWorks: {
      badge: 'Miten se toimii',
      title: 'Käyttövalmis muutamassa minuutissa',
      subtitle: 'Ei asennusta, ei sovelluskauppaa. Avaa vain selain ja aloita seuranta.',
      steps: {
        open: { title: 'Avaa verkkosovellus', description: 'Siirry osoitteeseen navigacija.easydrive.app millä tahansa modernilla selaimella puhelimellasi tai tietokoneellasi. Asennusta ei tarvita.' },
        profile: { title: 'Määritä profiilisi', description: 'Kirjaudu tilillesi, luo uusi kuljettajaprofiili tai jatka anonyymisti. Sinun valintasi.' },
        server: { title: 'Syötä palvelimen tiedot', description: 'Syötä Traccar OsmAnd -palvelimesi URL ja laitetunnus. Sovellus muistaa asetuksesi seuraavaa kertaa varten.' },
        track: { title: 'Aloita seuranta', description: 'Paina LIVE-painiketta ja sijaintisi alkaa lähettää 10 sekunnin välein. Vahvista lähetykset reaaliajassa.' },
      },
      tryNow: 'Kokeile nyt — se on ilmainen',
    },
    webOnly: {
      badge: 'Vain verkkosovellus',
      title: 'Ei sovelluskauppaa.',
      titleHighlight: 'Avaa ja seuraa.',
      description: 'EasyDrive on progressiivinen verkkosovellus (PWA) — se toimii kokonaan selaimessasi. Ei latauksia, ei päivitysten hallintaa, ei tallennustilan käyttöä.',
      benefits: [
        'Toimii millä tahansa laitteella, jossa on moderni selain',
        'Google Play tai App Store ei tarvita',
        'Aina ajan tasalla — ei manuaalisia päivityksiä',
        'Lisää aloitusnäytölle sovelluksen kaltaista kokemusta varten',
        'Taustapaikannus service workerin avulla',
      ],
      cards: {
        browser: { title: 'Selainpohjainen', desc: 'Avaa Chromessa, Safarissa, Firefoxissa — mikä tahansa selain toimii' },
        mobile: { title: 'Mobiilivalmiinen', desc: 'Optimoitu älypuhelimille kosketusystävällisellä käyttöliittymällä' },
        instant: { title: 'Välitön pääsy', desc: 'Ei odottelua latausten tai asennusten vuoksi' },
        secure: { title: 'Turvallinen', desc: 'HTTPS-salattu, tietosi pysyvät yksityisinä' },
      },
    },
    traccarSection: {
      title: 'Traccar ja OsmAnd -yhteensopiva',
      description: 'Käytätkö jo Traccar-palvelinta? EasyDrive yhdistää suoraan olemassa olevaan infrastruktuuriisi OsmAnd-protokollan avulla. Syötä vain palvelimen URL ja laitetunnus.',
      tags: { osmand: 'OsmAnd-protokolla', traccar: 'Traccar-palvelin', realtime: 'Reaaliaikaiset päivitykset' },
    },
    contact: {
      badge: 'Ota yhteyttä',
      title: 'Ota yhteyttä',
      description: 'Onko sinulla kysymyksiä EasyDrivesta? Haluatko ostaa lisenssin tai saada tukea? Ota yhteyttä suoraan haluamallasi viestintäalustalla.',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Chatta kanssamme',
      telegram: 'Telegram',
      telegramSub: 'Lähetä viesti',
      availability: 'Saatavilla maanantai – perjantai, 8:00 – 18:00 CET',
    },
    cta: {
      title: 'Aloita seuranta tänään',
      description: 'Avaa EasyDrive selaimessasi ja aloita reaaliaikaisen sijaintisi lähettäminen Traccar-palvelimellesi alle 2 minuutissa.',
      openApp: 'Avaa EasyDrive',
      contactSales: 'Ota yhteyttä myyntiin',
    },
    footer: {
      description: 'Henkilökohtainen GPS-seuranta, joka lähettää sijaintisi reaaliajassa 10 sekunnin välein Traccar OsmAnd -palvelimellesi.',
      quickLinks: 'Pikalinkit',
      links: { openApp: 'Avaa sovellus', features: 'Ominaisuudet', howItWorks: 'Miten se toimii', contact: 'Yhteystiedot' },
      contactTitle: 'Yhteystiedot',
      copyright: 'EasyDrive GPS-seuranta. Kaikki oikeudet pidätetään.',
      serviceOnline: 'Palvelu verkossa',
    },
  },
  hi: {
    nav: { features: 'विशेषताएं', howItWorks: 'यह कैसे काम करता है', contact: 'संपर्क', openApp: 'ऐप खोलें' },
    hero: {
      badge: 'लाइव GPS ट्रैकिंग — वेब ऐप',
      tagline: 'GPS ट्रैकर',
      subtitle: 'Traccar को लाइव स्थान',
      description: 'व्यक्तिगत GPS ट्रैकर जो हर 10 सेकंड में आपका लाइव स्थान आपके Traccar OsmAnd सर्वर पर भेजता है। रियल टाइम में ट्रैकिंग शुरू करें, ट्रैक करें और ट्रांसमिशन की पुष्टि करें।',
      openWebApp: 'वेब ऐप खोलें',
      learnMore: 'अधिक जानें',
      stats: { updateInterval: 'अपडेट अंतराल', webBased: 'वेब आधारित', liveTracking: 'लाइव ट्रैकिंग' },
      scroll: 'स्क्रॉल करें',
    },
    preview: { title: 'इसे काम करते देखें', subtitle: 'ड्राइवरों के लिए डिज़ाइन किया गया स्वच्छ, सहज इंटरफ़ेस' },
    features: {
      badge: 'विशेषताएं',
      title: 'आपको जो चाहिए वह सब',
      subtitle: 'ड्राइवरों, फ्लीट मैनेजरों और उन सभी के लिए एक पूर्ण GPS ट्रैकिंग समाधान जिन्हें विश्वसनीय रियल-टाइम स्थान साझाकरण की आवश्यकता है।',
      items: {
        liveGps: { title: 'लाइव GPS ट्रैकिंग', description: 'हर 10 सेकंड में आपका सटीक स्थान रियल टाइम में आपके Traccar OsmAnd सर्वर पर भेजता है।' },
        webApp: { title: 'वेब ऐप — कोई इंस्टॉलेशन नहीं', description: 'सीधे आपके ब्राउज़र में काम करता है। ऐप स्टोर से डाउनलोड की जरूरत नहीं। खोलें और तुरंत ट्रैकिंग शुरू करें।' },
        traccar: { title: 'Traccar संगत', description: 'आपके मौजूदा Traccar / OsmAnd सर्वर के साथ निर्बाध रूप से एकीकृत होता है। बस अपना सर्वर URL और डिवाइस ID दर्ज करें।' },
        updates: { title: '10-सेकंड अपडेट', description: 'हर 10 सेकंड में अल्ट्रा-फ्रीक्वेंट पोजीशन अपडेट सुनिश्चित करते हैं कि आपके पास हमेशा सबसे वर्तमान स्थान डेटा हो।' },
        profiles: { title: 'ड्राइवर प्रोफाइल', description: 'अपने खाते से साइन इन करें, एक नामित ड्राइवर प्रोफाइल बनाएं, या केवल अपने डिवाइस ID का उपयोग करके गुमनाम रूप से ट्रैक करें।' },
        radio: { title: 'ऑनलाइन रेडियो', description: 'लंबी ड्राइव के दौरान आपका मनोरंजन करने के लिए बिल्ट-इन ऑनलाइन रेडियो प्लेयर — सब एक ऐप में।' },
      },
    },
    howItWorks: {
      badge: 'यह कैसे काम करता है',
      title: 'मिनटों में तैयार',
      subtitle: 'कोई इंस्टॉलेशन नहीं, कोई ऐप स्टोर नहीं। बस अपना ब्राउज़र खोलें और ट्रैकिंग शुरू करें।',
      steps: {
        open: { title: 'वेब ऐप खोलें', description: 'अपने फोन या कंप्यूटर पर किसी भी आधुनिक ब्राउज़र में navigacija.easydrive.app पर जाएं। कोई इंस्टॉलेशन आवश्यक नहीं।' },
        profile: { title: 'अपना प्रोफाइल सेट करें', description: 'अपने खाते में साइन इन करें, एक नया ड्राइवर प्रोफाइल बनाएं, या गुमनाम रूप से जारी रखें। आपकी पसंद।' },
        server: { title: 'सर्वर विवरण दर्ज करें', description: 'अपना Traccar OsmAnd सर्वर URL और डिवाइस ID दर्ज करें। ऐप अगली बार के लिए आपकी सेटिंग याद रखता है।' },
        track: { title: 'ट्रैकिंग शुरू करें', description: 'LIVE बटन दबाएं और आपका स्थान हर 10 सेकंड में ट्रांसमिट होना शुरू हो जाता है। रियल टाइम में ट्रांसमिशन की पुष्टि करें।' },
      },
      tryNow: 'अभी आज़माएं — यह मुफ़्त है',
    },
    webOnly: {
      badge: 'केवल वेब ऐप',
      title: 'कोई ऐप स्टोर नहीं।',
      titleHighlight: 'बस खोलें और ट्रैक करें।',
      description: 'EasyDrive एक प्रोग्रेसिव वेब ऐप (PWA) है — यह पूरी तरह से आपके ब्राउज़र में चलता है। कोई डाउनलोड नहीं, कोई अपडेट प्रबंधन नहीं, कोई स्टोरेज स्पेस उपयोग नहीं।',
      benefits: [
        'आधुनिक ब्राउज़र वाले किसी भी डिवाइस पर काम करता है',
        'Google Play या App Store की आवश्यकता नहीं',
        'हमेशा अप-टू-डेट — कोई मैनुअल अपडेट नहीं',
        'ऐप जैसे अनुभव के लिए होम स्क्रीन में जोड़ें',
        'service worker के साथ बैकग्राउंड ट्रैकिंग',
      ],
      cards: {
        browser: { title: 'ब्राउज़र-आधारित', desc: 'Chrome, Safari, Firefox में खोलें — कोई भी ब्राउज़र काम करता है' },
        mobile: { title: 'मोबाइल तैयार', desc: 'टच-फ्रेंडली UI के साथ स्मार्टफोन के लिए अनुकूलित' },
        instant: { title: 'तत्काल पहुंच', desc: 'डाउनलोड या इंस्टॉलेशन का इंतजार नहीं' },
        secure: { title: 'सुरक्षित', desc: 'HTTPS एन्क्रिप्टेड, आपका डेटा निजी रहता है' },
      },
    },
    traccarSection: {
      title: 'Traccar और OsmAnd संगत',
      description: 'क्या आप पहले से Traccar सर्वर चला रहे हैं? EasyDrive OsmAnd प्रोटोकॉल का उपयोग करके आपके मौजूदा इंफ्रास्ट्रक्चर से सीधे जुड़ता है। बस अपना सर्वर URL और डिवाइस ID दर्ज करें।',
      tags: { osmand: 'OsmAnd प्रोटोकॉल', traccar: 'Traccar सर्वर', realtime: 'रियल-टाइम अपडेट' },
    },
    contact: {
      badge: 'हमसे संपर्क करें',
      title: 'संपर्क में रहें',
      description: 'EasyDrive के बारे में प्रश्न हैं? लाइसेंस खरीदना चाहते हैं या सहायता प्राप्त करना चाहते हैं? अपने पसंदीदा मैसेजिंग प्लेटफॉर्म पर हमसे सीधे संपर्क करें।',
      whatsapp: 'WhatsApp',
      whatsappSub: 'हमसे चैट करें',
      telegram: 'Telegram',
      telegramSub: 'हमें संदेश भेजें',
      availability: 'सोमवार – शुक्रवार, 8:00 – 18:00 CET उपलब्ध',
    },
    cta: {
      title: 'आज ट्रैकिंग शुरू करें',
      description: 'अपने ब्राउज़र में EasyDrive खोलें और 2 मिनट से कम में अपना लाइव स्थान Traccar सर्वर पर भेजना शुरू करें।',
      openApp: 'EasyDrive खोलें',
      contactSales: 'बिक्री से संपर्क करें',
    },
    footer: {
      description: 'व्यक्तिगत GPS ट्रैकर जो हर 10 सेकंड में आपका लाइव स्थान आपके Traccar OsmAnd सर्वर पर भेजता है।',
      quickLinks: 'त्वरित लिंक',
      links: { openApp: 'ऐप खोलें', features: 'विशेषताएं', howItWorks: 'यह कैसे काम करता है', contact: 'संपर्क' },
      contactTitle: 'संपर्क',
      copyright: 'EasyDrive GPS ट्रैकर। सर्वाधिकार सुरक्षित।',
      serviceOnline: 'सेवा ऑनलाइन',
    },
  },
};

export function getTranslations(langCode: string): Translations {
  return translations[langCode] || translations['en'];
}
